import React, { useCallback, useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/Button";
import { Label } from "../../components/ui/Input";
import { api, extractError } from "../../lib/api";
import { cn } from "../../lib/utils";
import AiBusyOverlay from "../../components/AiBusyOverlay";
import HelpAnchor from "../../components/HelpAnchor";
import {
  ArrowLeft,
  ArrowRight,
  ArrowsClockwise,
  ClockCounterClockwise,
  Info,
  Eye,
  Briefcase,
  Warning,
  List,
  Rows,
  CaretDown,
  Calculator,
  Plus,
  Sparkle,
} from "@phosphor-icons/react";
import {
  ALLOWED_EXT,
  ALLOWED_MIME,
  MAX_FILE_BYTES,
  MAX_FILES,
  applyKvFactorRule,
  mapBackendItem,
  computeEffectiveTotal,
  isAngestelltPosition,
  needsEquivalenceReview,
  hasUnresolvedPraktikum,
  hasUnresolvedQualityWarning,
  rowNeedsAttention,
} from "./shared";
import FilterChips from "./step3/FilterChips";
import QualityBanner from "./step3/QualityBanner";
import CompareModal from "./step3/CompareModal";
import VordienstRow, { EmptyRow } from "./step3/VordienstRow";
import Timeline from "./step3/Timeline";
import BulkActionBar from "./step3/BulkActionBar";
import KvHinweisBanner from "./step3/KvHinweisBanner";
import FileUploadPane from "./step3/FileUploadPane";
import AusbildungsBlock from "./step3/AusbildungsBlock";
import BerechnungsdetailsModal from "./step3/BerechnungsdetailsModal";
import ResetDialog from "./step3/ResetDialog";
import { isFeatureEnabled } from "../../config/features";
import { useDebouncedAutoRecalc } from "../../hooks/useDebouncedAutoRecalc";
import { isDemoActive, getActiveDemoCaseId } from "../../demo/demoInterceptor";
import { getDemoCase } from "../../demo/cases";

// Audit-Fix P2 — stabile Leer-Referenzen für memo-Props. Ohne diese erzeugt
// `|| {}` / `|| []` pro Render ein NEUES Objekt und bricht den
// shallow-compare von React.memo(VordienstRow).
const EMPTY_OBJ = Object.freeze({});
const EMPTY_ARR = Object.freeze([]);

export default function StepThree({
  selectedKv,
  suggestedGroup,
  taetigkeitKurz,
  stichtag,
  rows,
  setRows,
  ausbildungszeiten,
  setAusbildungszeiten,
  setBackendAggregate,
  backendAggregate,
  onBack,
  onNext,
}) {
  // Iter 19.63.5 — Mirror of backend's _patch_zusammenfassung_count.
  // Replaces patterns like "Von 51 erfassten Zeiträumen" → "Von 57 erfassten
  // Zeiträumen (automatisch konsolidiert auf 51 Zeilen)" — only when the
  // matched number equals merged_count (so we don't accidentally rewrite
  // unrelated counts like "10 Positionen sind anrechenbar").
  //
  // Iter 19.63.18 — Falls der Regex nicht greift (LLM nutzt eine andere
  // Phrasierung wie z.B. „Aus 51 Positionen sind 10 anrechenbar"),
  // prepended wir einen deterministischen Konsolidierungs-Hinweis.
  // Idempotent — kein Doppel-Prepend, wenn Info bereits enthalten.
  function patchZusammenfassungCount(text, rawCount, mergedCount) {
    if (!text || !rawCount || !mergedCount || rawCount <= mergedCount) return text;
    const pat = /(\b\d+)\s+(erfassten?|extrahierten?|erkannten?|aufgenommenen?)\s+(Zeitr[äa]um\w*|Position\w*|Beschäftigung\w*|Eintr[äa]g\w*|Zeile\w*)/i;
    const patched = text.replace(pat, (m, n, verb, noun) => {
      if (parseInt(n, 10) !== mergedCount) return m;
      return `${rawCount} ${verb} ${noun} (automatisch konsolidiert auf ${mergedCount} Zeilen)`;
    });
    const lower = patched.toLowerCase();
    const alreadyHasInfo = lower.includes("konsolidiert") && patched.includes(String(rawCount));
    if (alreadyHasInfo) return patched;
    const prefix = `Im SV-Auszug wurden ${rawCount} Zeiträume erfasst und automatisch auf ${mergedCount} Zeilen konsolidiert. `;
    return (prefix + patched).slice(0, 600);
  }
  const inputRef = useRef(null);
  // Iter 20.09 — Fake-File-Vorbelegung im Demo-Modus. Ein leichtgewichtiges
  // Plain-Object simuliert eine bereits hochgeladene Beispiel-Datei. Der
  // FileUploadPane rendert nur name/size — kein echtes Blob nötig. Der
  // `runAiExtract`-Handler prüft `isDemoActive()` und short-circuited den
  // Upload, da die Vordienstzeiten bereits im State pre-populated sind.
  const [files, setFiles] = useState(() => {
    if (!isDemoActive()) return [];
    const kase = getDemoCase(getActiveDemoCaseId());
    const meta = kase?.extractResponse?.files?.[0];
    if (!meta) return [];
    return [
      {
        name: meta.name,
        size: meta.size,
        type: "application/pdf",
        __demo: true,
      },
    ];
  });
  const [extracting, setExtracting] = useState(false);
  const [extractStage, setExtractStage] = useState(null); // "read" | "refine" | null
  // Iter 19.63.46 / 19.63.55 / 19.63.56 — Progress-State.
  // `pollDebug` = optionaler Status-String (für Diagnose).
  // `pollProgress` = die REALE Zahl vom Backend (jetzt im Sekunden-Takt
  // dank Backend-seitigem Creep, Iter 19.64.03+).
  // `displayProgress` = was wir dem User zeigen — seit Iter 19.64.07
  // identisch zu pollProgress. Vorher gab es eine separate Phantom-Creep-
  // Schicht, die zwischen Backend-Updates linear hochzog (+1 %/s); das
  // war nötig, solange das Backend Lücken von 60-90 s zwischen Updates
  // ließ. Mit dem neuen Backend-Linear-Creep (1 Update/s) ist die
  // Phantom-Schicht überflüssig und sogar schädlich: sie raste mit
  // +1 %/s gegen den Cap (next_phase - 1) — bei Backend-Start auf 15 %
  // war der Cap bei 64, also der Bar nach 49 s bei 64 % unabhängig
  // davon, was der LLM-Call gerade macht. Mit dem Direkt-Mirror sieht
  // der User exakt das, was das Backend liefert; die CSS-Transition
  // `duration-500 ease-out` an der Bar smoothed die Sekunden-Ticks.
  const [pollDebug, setPollDebug] = useState(null);
  const [pollProgress, setPollProgress] = useState(null);
  const [displayProgress, setDisplayProgress] = useState(null);
  const [pollLabel, setPollLabel] = useState("");

  // Iter 19.63.103 — Cap-Bypass-Latching: cacht die letzten erfolgreichen
  // Backend-Werte (cap_bypass_rules, cap_months, was_cap_reached_at_last_recalc),
  // damit beim Toggle der „Selber AG (Bypass)"-Checkbox der Display-Status
  // nicht flackert, während ein Recalc läuft.
  const [latchedCapBypassInfo, setLatchedCapBypassInfo] = useState(null);
  // Iter 19.68.7 — Filter-Chip-State für die Vordienst-Liste.
  // Reine Frontend-Ansicht — Math-Engine und Step-4-Payload bleiben
  // unberührt (alle Zeilen werden weiter berechnet und transportiert).
  const [vordienstFilter, setVordienstFilter] = useState("all");
  // Iter 19.64.07 — Direkt-Mirror statt Phantom-Creep.
  useEffect(() => {
    setDisplayProgress(pollProgress);
  }, [pollProgress]);
  const [reviewing] = useState(false);
  const [error, setError] = useState("");
  const [aiInfo, setAiInfo] = useState(null);
  const [sourceBlobs, setSourceBlobs] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [uploadCollapsed, setUploadCollapsed] = useState(false);
  // Iter 19.63.4 — Berechnungsdetails-Modal.
  const [showBerechnung, setShowBerechnung] = useState(false);
  // Iter 20.03.P5-fix Runde 7 — Quality-Warning-Banner ist per Default
  // eingeklappt, damit das Amber-Warn-Konzert oben nicht so laut ist
  // (UX-Audit P0: Amber-Inflation). User klickt bewusst „Details ein-
  // blenden", wenn er die Chips wirklich braucht.
  const [qualityWarningExpanded, setQualityWarningExpanded] = useState(false);
  // Iter 19.63.66 — Reset-Dialog (Soft/Hard Reset).
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  // Iter 19.63.66 / Runde 10 (button removed) — busy-State des ehemaligen
  // Recalc-Buttons. Bleibt als State weil `runRecalcSum()` intern noch
  // KEIN KI-Call). Separat von `reviewing`, damit kein KI-Overlay erscheint.
  const [recalculating, setRecalculating] = useState(false);
  const abortRef = useRef(null);
  // Iter 20.03.P5-fix Runde 12 (Race-Bugfix Toggle-Sequenz) — User-Edit-
  // Generations-Counter. Wird bei JEDER User-initiierten Row-Änderung
  // synchron hochgezählt (Toggle, Faktor-Edit, Datum-Edit, Zeile-hinzu-
  // fügen, Zeile-löschen, Bulk-Action, Reset). Der `runRecalcSum` snapshotet
  // den Counter VOR dem POST und vergleicht nach der Response. Wenn der
  // Counter zwischenzeitlich gestiegen ist, wird die Response komplett
  // verworfen (statt nur pro-Row-drift zu erkennen). Das ist zuverlässiger
  // als das alte `anyDrift`-Pattern, welches darauf angewiesen war, dass
  // React die `setRows((prev) => …)`-Updater synchron aufruft — was in
  // React 18 aber nicht immer garantiert ist. Symptom bei schnellem
  // Anrechnen-Toggle AUS→EIN: Nach der Response wurde der KPI-Wert des
  // AUS-Zustands übernommen, obwohl der Switch schon wieder AN war.
  const userEditGenerationRef = useRef(0);
  // Iter 20.03.P5-fix Runde 13 (KPI-Layout-Stabilität 2) — Letzter bekannter
  // Backend-Aggregate-Display-Wert. Wird bei dirty/running als Fallback in
  // der Kachel verwendet (statt „Neuberechnung nötig"-Text), damit die
  // Kachel-Breite/Höhe während des Recalcs konstant bleibt. Der pulsierende
  // Sky-Dot übernimmt die Signalisierung dass gerade neu gerechnet wird.
  const lastKnownDisplayRef = useRef("");

  // Revoke blob URLs on unmount to avoid memory leaks.
  useEffect(() => {
    return () => {
      for (const b of sourceBlobs) {
        try { URL.revokeObjectURL(b.url); } catch (_e) { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelAiBusy = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

  const addFiles = (incoming) => {
    setError("");
    const next = [...files];
    for (const f of incoming) {
      if (next.length >= MAX_FILES) {
        setError(`Maximal ${MAX_FILES} Dateien pro Anfrage.`);
        break;
      }
      const lower = (f.name || "").toLowerCase();
      const okExt = ALLOWED_EXT.some((e) => lower.endsWith(e));
      const okMime = ALLOWED_MIME.includes(f.type);
      if (!okExt && !okMime) {
        setError(`„${f.name}" hat ein nicht erlaubtes Format. Zulässig sind PDF, JPG und PNG.`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        setError(`„${f.name}" überschreitet 15 MB.`);
        continue;
      }
      if (next.find((x) => x.name === f.name && x.size === f.size)) continue;
      next.push(f);
    }
    setFiles(next);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onFilePick = (e) => addFiles(Array.from(e.target.files || []));
  const onDrop = (e) => {
    e.preventDefault();
    addFiles(Array.from(e.dataTransfer.files || []));
  };
  const onDragOver = (e) => e.preventDefault();
  const removeFile = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
    setError("");
  };

  const runAiExtract = async () => {
    if (files.length === 0) {
      setError("Bitte laden Sie mindestens einen Versicherungsdatenauszug oder ein Zeugnis hoch.");
      return;
    }
    // Iter 20.09 — Demo-Short-Circuit: In der Demo sind die Vordienstzeiten
    // bereits im State pre-populated. Der „Datei auswerten"-Button ist
    // sichtbar (Interaktion echt), aber tut hier nur einen freundlichen
    // Toast statt einen echten Job-Endpoint aufzurufen.
    if (isDemoActive()) {
      toast.info(
        "Beispiel-Datei — die Vordienstzeiten sind in der Demo bereits ausgewertet.",
        { duration: 4000 }
      );
      return;
    }
    setError("");
    setExtracting(true);
    setExtractStage("read");
    setPollDebug("upload"); // sichtbar: initialer POST läuft
    setPollProgress(0); // Iter 19.63.57 — Sofort sichtbare 0%-Bar; Backend snappt auf echte Werte
    setDisplayProgress(0);
    setPollLabel("Vorbereitung …");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const fd = new FormData();
      fd.append("kv_id", selectedKv.id);
      fd.append("consent_ack", "true");
      fd.append("suggested_group", suggestedGroup || "");
      fd.append("taetigkeit_kurz", taetigkeitKurz || "");
      if (stichtag) fd.append("stichtag", stichtag);
      for (const f of files) fd.append("files", f, f.name);

      // Iter 19.63.43 — Background-Job-Modus: Frontend startet einen Job,
      // pollt /vordienst-job/{id} statt einer langen Single-HTTP-Verbindung.
      // Eliminiert die Timeout-Klasse (K8s-Ingress + CloudFront cancelten
      // Connections nach ~60-90s; sync-Pfad konnte 170s+ nicht überstehen).
      const { data: startData } = await api.post(
        "/tool/vordienst-job/start",
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          // Iter 19.63.45 — Initial-POST nur für Upload + Job-Start.
          // 300s deckt sehr langsame Multipart-Uploads (Mobilnetz) und
          // CloudFront-Buffering ab. Echter Backend-Code returnt in <100ms.
          timeout: 300_000,
          signal: controller.signal,
        },
      );
      // eslint-disable-next-line no-console
      console.log("[vordienst-job] POST returned:", startData);
      const jobId = startData?.job_id;
      // eslint-disable-next-line no-console
      console.log("[vordienst-job] jobId =", jobId);
      if (!jobId) throw new Error("Job konnte nicht gestartet werden.");
      setPollDebug("connect"); // SSE-Verbindung wird aufgebaut

      // Iter 19.63.54 — Long-Polling statt SSE.
      //
      // SSE-Migration (Iter 51-53) wurde rückgängig gemacht: EventSource +
      // Cloudflare/Ingress kombiniert war zu fragil (auto-reconnect ignoriert
      // `retry:`-Field; `event: error` als reservierter Browser-Name; CF
      // bufferte Heartbeats unzuverlässig).
      //
      // Long-Polling-Modell:
      //   GET /tool/vordienst-job/{id}?wait=20
      //   Backend wartet bis zu 20s auf nächstes Event, returnt dann
      //   aktuellen Status. Frontend loopt einfach bis status final.
      //
      // Vorteile:
      //   • Reines HTTP — kein EventSource-Komplex
      //   • Browser-Reconnect-Loops strukturell ausgeschlossen
      //   • Server-Push-Latenz (Backend signalisiert sofort bei Event)
      //   • Robust gegen jegliche Reverse-Proxies
      const STREAM_MAX_DURATION_MS = 10 * 60 * 1000;
      const POLL_WAIT_SECONDS = 20;
      const streamStart = Date.now();
      let jobResult = null;
      let lastStage = "read";
      let pollIdx = 0;
      // Iter 19.63.58 — `since_progress` ist DER Schlüssel zum echten
      // Long-Polling. Backend prüft: wenn current_progress > since_progress
      // → sofortiger Push. Sonst subscribed der Endpoint und wartet bis zum
      // nächsten set_progress-Event. Ohne diesen Param defaulted das
      // Backend auf -1 → returned IMMER sofort → Busy-Loop → identische
      // URLs werden durch Browser/CF dedupliziert → in der Praxis kommt
      // nur 1 Poll am Backend an, blockiert dort jedoch bis Job done ist.
      let sinceProgress = -1;
      while (true) {
        if (controller.signal.aborted) {
          throw new DOMException("Vorgang abgebrochen.", "AbortError");
        }
        if (Date.now() - streamStart > STREAM_MAX_DURATION_MS) {
          throw new Error(
            "Die Verarbeitung dauert länger als erwartet. Bitte versuchen Sie es erneut "
            + "oder laden Sie ein kleineres Dokument hoch.",
          );
        }
        pollIdx += 1;
        setPollDebug(`poll-${pollIdx}-wait`);
        let pollData;
        try {
          // Cache-Buster `_t=…` verhindert, dass Browser/CDN identische
          // GETs koaleszieren oder cachen — wir wollen JEDE Anfrage neu.
          const resp = await fetch(
            `/api/tool/vordienst-job/${encodeURIComponent(jobId)}`
              + `?wait=${POLL_WAIT_SECONDS}`
              + `&since_progress=${sinceProgress}`
              + `&_t=${Date.now()}`,
            {
              method: "GET",
              credentials: "include",
              cache: "no-store",
              signal: controller.signal,
            },
          );
          if (!resp.ok) {
            // Transient 5xx → kurze Pause und Retry.
            if (resp.status >= 500 && resp.status < 600) {
              setPollDebug(`poll-${pollIdx}-retry (${resp.status})`);
              await new Promise((res) => setTimeout(res, 2000));
              continue;
            }
            if (resp.status === 404) {
              throw new Error("Job nicht mehr verfügbar — bitte erneut hochladen.");
            }
            throw new Error(`Polling-GET HTTP ${resp.status}`);
          }
          pollData = await resp.json();
        } catch (fetchErr) {
          if (controller.signal.aborted) throw fetchErr;
          // Network glitch → short pause and retry.
          setPollDebug(`poll-${pollIdx}-net-retry`);
          await new Promise((res) => setTimeout(res, 2000));
          continue;
        }
        setPollDebug(`poll-${pollIdx}-${pollData?.status || "?"}`);
        // Iter 19.63.55 — Echte Progress-Werte vom Backend aufnehmen.
        if (typeof pollData?.progress === "number") {
          setPollProgress(pollData.progress);
          // Iter 19.63.58 — Track `since_progress` für nächsten Long-Poll.
          // Damit kennt das Backend exakt, was der Client schon weiß, und
          // returnt SOFORT bei jedem neueren progress-Wert.
          sinceProgress = pollData.progress;
        }
        if (typeof pollData?.progress_label === "string" && pollData.progress_label) {
          setPollLabel(pollData.progress_label);
        }
        // eslint-disable-next-line no-console
        console.log(`[vordienst-job ${jobId.slice(0,8)}] poll[${pollIdx}]:`, pollData?.status, pollData?.progress, pollData?.progress_label);

        // Stage-Mapping.
        const lbl = (pollData?.progress_label || "").toLowerCase();
        if (lbl.includes("anrechenbarkeit")) {
          if (lastStage !== "refine") {
            setExtractStage("refine");
            lastStage = "refine";
          }
        } else if (lbl.includes("dokumente") || lbl.includes("rendere") || lbl.includes("prüfe")) {
          if (lastStage !== "read") {
            setExtractStage("read");
            lastStage = "read";
          }
        }

        if (pollData?.status === "done") {
          jobResult = pollData.result;
          // Iter 19.64.02 — Final-Frame-Bühne für den 100 %-State.
          // Selbst wenn das Backend `set_progress(100, "Fertig")` aufgerufen
          // hat, liefert der vorherige Poll oft noch einen Zwischenwert
          // (z. B. 70 %) und der done-Status kommt parallel. Wir setzen
          // den sichtbaren Endwert defensiv selbst auf 100 %, damit der
          // CSS-Übergang `duration-500 ease-out` den Balken sichtbar
          // hochfüllen kann, und halten das Overlay anschließend nochmal
          // ~500 ms im 100 %-Zustand offen, damit „Fertig" bewusst
          // wahrnehmbar ist (gesamter Final-Frame ≈ 1 s).
          setPollProgress(100);
          setPollLabel("Fertig");
          await new Promise((res) => setTimeout(res, 1000));
          break;
        }
        if (pollData?.status === "error" || pollData?.status === "cancelled") {
          throw new Error(pollData?.error || "Die Auswertung ist fehlgeschlagen.");
        }
        // status === 'pending' or 'running' → loop continues immediately
        // (Backend hat schon bis zu 20s gewartet, kein zusätzlicher Sleep nötig).
      }

      // Job-Result-Form: { extract: {...}, review: {...}|null, ... }
      // — wir mappen zurück auf das ursprüngliche Format,
      // damit der nachfolgende Code unverändert bleibt.
      const result = jobResult?.extract;
      if (!result) throw new Error("Unerwartete Antwort vom System.");
      const rawItems = (result.items || []).map((it) => mapBackendItem(it));
      const revResult = jobResult?.review || null;
      const reviewSucceeded = Boolean(revResult?.items?.length);

      // Iter 19.43 — Zwei-Stufen-Pipeline ist jetzt SERIELL über zwei HTTP-Calls:
      // 1) /extract-vordienstzeiten (Gemini liest die Daten + deterministische
      //    Konsolidierung im Backend — siehe Iter 19.63.3)
      // 2) /review-vordienstzeiten?consolidate=false (Claude veredelt nur noch
      //    pro-Zeile die Anrechenbarkeit + Begründungen, OHNE Merging)
      // User sieht weiterhin nur EIN Overlay — wir setzen die UI-Felder erst
      // NACH beiden Calls, damit nichts „zwischendurch" auftaucht.
      let finalItems = rawItems;
      let finalBackendAggregate = result.anrechenbare_dauer
        ? {
            display: result.anrechenbare_dauer.display || "",
            jahre: result.anrechenbare_dauer.jahre || 0,
            monate: result.anrechenbare_dauer.monate || 0,
            tage: result.anrechenbare_dauer.tage || 0,
            // Iter 19.63.39 — Backend liefert jetzt `totalDays` direkt in
            // `anrechenbare_dauer`. Fallback (FE-Recompute aus Y/M/T) bleibt
            // für Backwards-Compat, falls Backend-Response noch alt ist.
            totalDays: typeof result.anrechenbare_dauer.totalDays === "number"
              ? result.anrechenbare_dauer.totalDays
              : (result.anrechenbare_dauer.jahre || 0) * 360
                + (result.anrechenbare_dauer.monate || 0) * 30
                + (result.anrechenbare_dauer.tage || 0),
            // Iter 19.63.28 — Roh-Aggregat (Pre-Cap, Pre-Overlap) für
            // Sidebar-„Gesamt: X J Y M Z T"-Sub-Text bei Cap-Hit. Kommt
            // direkt vom Backend, keine FE-Math mehr nötig.
            roh: result.anrechenbare_dauer_roh
              ? {
                  display: result.anrechenbare_dauer_roh.display || "",
                  totalDays: result.anrechenbare_dauer_roh.totalDays || 0,
                }
              : null,
            // Iter 19.63.35 — Post-Overlap-Pre-Cap-Aggregat: Diese Summe
            // zeigt, was anrechenbar WÄRE, wenn der Cap nicht greifen würde,
            // aber Überlappungen schon respektiert sind. Ist die korrekte
            // User-Erwartung für die Sidebar-„Gesamt"-Anzeige.
            post_overlap_pre_cap: result.anrechenbare_dauer_post_overlap_pre_cap
              ? {
                  display: result.anrechenbare_dauer_post_overlap_pre_cap.display || "",
                  totalDays: result.anrechenbare_dauer_post_overlap_pre_cap.totalDays || 0,
                }
              : null,
            exceeded: (result.cap_months || 0) > 0
              && (result.anrechenbare_dauer_post_overlap_pre_cap?.totalDays
                  || result.anrechenbare_dauer_roh?.totalDays || 0)
                 > (result.anrechenbare_dauer.jahre * 360
                    + result.anrechenbare_dauer.monate * 30
                    + result.anrechenbare_dauer.tage) + 5,
            // Iter 19.63.101 — Cap-Bypass-Aggregat + Regel-Konfiguration.
            bypass: result.anrechenbare_dauer_bypass
              ? {
                  display: result.anrechenbare_dauer_bypass.display || "",
                  jahre: result.anrechenbare_dauer_bypass.jahre || 0,
                  monate: result.anrechenbare_dauer_bypass.monate || 0,
                  tage: result.anrechenbare_dauer_bypass.tage || 0,
                  totalDays: result.anrechenbare_dauer_bypass.totalDays || 0,
                }
              : null,
            cap_bypass_rules: result.cap_bypass_rules || null,
            cap_months: result.cap_months || 0,
          }
        : null;
      let finalAiInfo = {
        zusammenfassung: result.zusammenfassung || "",
        hinweise: result.hinweise || [],
        anrechenbare_monate: result.anrechenbare_monate || 0,
        quellenqualitaet: result.quellenqualitaet || null,
        count: rawItems.length,
        // Iter 19.63.5 — Konsolidierungs-Statistik (für Sidebar-Footer).
        consolidation: result.consolidation
          ? {
              rawCount: result.consolidation.raw_count || 0,
              mergedCount: result.consolidation.merged_count || 0,
              merges: result.consolidation.merges || 0,
            }
          : null,
      };
      const modelUsed = String(result.model || "").toLowerCase();
      const usedGemini = modelUsed.includes("gemini");
      if (rawItems.length > 0 && usedGemini && reviewSucceeded) {
        // Iter 19.63.43 — Review-Result kommt jetzt aus dem Job (kein
        // zweiter HTTP-Call mehr). Mapping-Logik ist unverändert.
        try {
          setExtractStage("refine");
          {
            // (vormals: const { data: rev } = await api.post("/tool/review-vordienstzeiten", …))
            if (revResult?.items?.length > 0) {
            finalItems = revResult.items.map((it, idx) =>
              mapBackendItem(it, rawItems[idx] || {})
            );
            finalAiInfo = {
              zusammenfassung: revResult.zusammenfassung || finalAiInfo.zusammenfassung,
              hinweise: revResult.hinweise || finalAiInfo.hinweise,
              anrechenbare_monate: revResult.anrechenbare_monate || finalAiInfo.anrechenbare_monate,
              quellenqualitaet: finalAiInfo.quellenqualitaet,
              count: finalItems.length,
              // Iter 19.63.5 — Consolidation kommt aus dem Extract, NICHT aus dem
              // Review (review läuft mit consolidate=false und macht kein Merging).
              consolidation: finalAiInfo.consolidation,
            };
            // Iter 19.63.5 — Re-patch zusammenfassung mit der raw/merged-Zahl,
            // falls Claude den Text neu geschrieben hat (z.B. "Von 51 erfassten…"
            // → "Von 57 erfassten Zeiträumen (konsolidiert auf 51 Zeilen)…").
            if (finalAiInfo.consolidation && finalAiInfo.consolidation.merges > 0) {
              finalAiInfo.zusammenfassung = patchZusammenfassungCount(
                finalAiInfo.zusammenfassung,
                finalAiInfo.consolidation.rawCount,
                finalAiInfo.consolidation.mergedCount,
              );
            }
            if (revResult.anrechenbare_dauer) {
              finalBackendAggregate = {
                display: revResult.anrechenbare_dauer.display || "",
                jahre: revResult.anrechenbare_dauer.jahre || 0,
                monate: revResult.anrechenbare_dauer.monate || 0,
                tage: revResult.anrechenbare_dauer.tage || 0,
                // Iter 19.63.39 — Backend-totalDays mit Fallback.
                totalDays: typeof revResult.anrechenbare_dauer.totalDays === "number"
                  ? revResult.anrechenbare_dauer.totalDays
                  : (revResult.anrechenbare_dauer.jahre || 0) * 360
                    + (revResult.anrechenbare_dauer.monate || 0) * 30
                    + (revResult.anrechenbare_dauer.tage || 0),
                // Iter 19.63.28 — Roh-Aggregat aus Review-Response.
                roh: revResult.anrechenbare_dauer_roh
                  ? {
                      display: revResult.anrechenbare_dauer_roh.display || "",
                      totalDays: revResult.anrechenbare_dauer_roh.totalDays || 0,
                    }
                  : null,
                // Iter 19.63.35 — Post-Overlap-Pre-Cap-Aggregat aus Review.
                post_overlap_pre_cap: revResult.anrechenbare_dauer_post_overlap_pre_cap
                  ? {
                      display: revResult.anrechenbare_dauer_post_overlap_pre_cap.display || "",
                      totalDays: revResult.anrechenbare_dauer_post_overlap_pre_cap.totalDays || 0,
                    }
                  : null,
                exceeded: (revResult.cap_months || 0) > 0
                  && (revResult.anrechenbare_dauer_post_overlap_pre_cap?.totalDays
                      || revResult.anrechenbare_dauer_roh?.totalDays || 0)
                     > (revResult.anrechenbare_dauer.jahre * 360
                        + revResult.anrechenbare_dauer.monate * 30
                        + revResult.anrechenbare_dauer.tage) + 5,
                // Iter 19.63.101 — Cap-Bypass-Aggregat + Regel-Konfiguration.
                bypass: revResult.anrechenbare_dauer_bypass
                  ? {
                      display: revResult.anrechenbare_dauer_bypass.display || "",
                      jahre: revResult.anrechenbare_dauer_bypass.jahre || 0,
                      monate: revResult.anrechenbare_dauer_bypass.monate || 0,
                      tage: revResult.anrechenbare_dauer_bypass.tage || 0,
                      totalDays: revResult.anrechenbare_dauer_bypass.totalDays || 0,
                    }
                  : null,
                cap_bypass_rules: revResult.cap_bypass_rules || null,
                cap_months: revResult.cap_months || 0,
              };
            }
            }
          }
        } catch (revErr) {
          // Bei Review-Fehler (Timeout, Parse-Fail, etc.) bleiben die
          // Gemini-Roh-Items bestehen — kein Blocker für den User.
          // eslint-disable-next-line no-console
          console.warn("[StepThree] consolidate-review fehlgeschlagen, behalte Gemini-Stand:", revErr);
        }
      }

      setRows(finalItems);
      // Cache the uploaded files as blob URLs so the user can open the
      // "Quelle vs. Extraktion" modal afterwards. Browser-side only —
      // never leaves the user's RAM, never persisted.
      setSourceBlobs((prev) => {
        for (const b of prev) {
          try { URL.revokeObjectURL(b.url); } catch (_e) { /* noop */ }
        }
        return files.map((f) => ({
          name: f.name,
          type: f.type || "application/octet-stream",
          size: f.size,
          url: URL.createObjectURL(f),
        }));
      });
      // Pre-fill Ausbildungs-rows with backend's auto-matches. Dedupe by
      // label so HAK matched in Step 2 isn't doubled when Step-3 file
      // upload finds it again. (User's choice 2026-02-13: silent merge,
      // toast informs about new additions only.)
      // Iter 19.63.43 — auto_matched_ausbildung kommt jetzt aus dem Job-Result.
      const autoAusb = Array.isArray(jobResult?.auto_matched_ausbildung)
        ? jobResult.auto_matched_ausbildung
        : [];
      if (autoAusb.length > 0 && setAusbildungszeiten) {
        const existing = ausbildungszeiten || [];
        const existingLabels = new Set(
          existing.map((r) => (r?.label || "").toLowerCase()),
        );
        const additions = autoAusb.filter(
          (it) => !existingLabels.has((it.label || "").toLowerCase()),
        );
        if (additions.length > 0) {
          setAusbildungszeiten([...existing, ...additions]);
          toast.info(
            `${additions.length} Ausbildungs-Bonuszeit${additions.length === 1 ? "" : "en"} aus dem hochgeladenen Dokument erkannt — bitte im Block unten prüfen.`,
          );
        }
      }
      // Iter 19.63.20 — Backend-autoritativen Aggregate-Wert hochreichen
      // ALS LETZTE State-Update. Sowohl `setRows` (via
      // `setVordienstzeitenWithInvalidate`) als auch `setAusbildungszeiten`
      // (via `setAusbildungszeitenWithInvalidate`) invalidieren
      // `backendAggregate` zu null. Wir müssen es DANACH setzen, sonst
      // gewinnt das letzte invalidate-Wrapper-null (z.B. bei
      // auto_matched_ausbildung-Treffern wie Martina Fall 1 mit
      // Lehrabschluss) und die UI zeigt fälschlich „Neuberechnung nötig".
      // React 18 batcht alle Updates, also gewinnt der LAST set call.
      if (typeof setBackendAggregate === "function") {
        setBackendAggregate(finalBackendAggregate);
      }
      // Iter 19.63.103 — Latch nach erfolgreichem Extract/Review.
      // Quelle: bevorzugt revResult (LLM-Review), sonst result (Roh-Extract).
      updateCapBypassLatch(revResult || result);
      setAiInfo(finalAiInfo);
      // Auto-collapse upload pane after a successful KI-extraction so the
      // user has more space for the rows. Caret-Toggle lets them re-expand.
      setUploadCollapsed(true);
      if (finalItems.length > 0) {
        toast.success(
          `${finalItems.length} Beschäftigungsverhältnis${finalItems.length === 1 ? "" : "se"} erkannt — bitte prüfen und ggf. anpassen.`,
        );
      } else {
        toast.info("Es konnten keine Beschäftigungszeiten erkannt werden. Bitte manuell ergänzen.");
      }
    } catch (err) {
      // Iter 19.63.51 — User-Cancel von 3 Quellen erkennen:
      // - axios CanceledError (alte sync-Pfade)
      // - native fetch AbortError (DOMException)
      // - unser eigener "Vorgang abgebrochen."-Throw aus dem SSE-Block
      const isCanceled = (
        err?.code === "ERR_CANCELED"
        || err?.name === "CanceledError"
        || err?.name === "AbortError"
        || (typeof err?.message === "string" && err.message.includes("abgebrochen"))
      );
      if (isCanceled) {
        toast.info("Vorgang abgebrochen.");
      } else {
        setError(extractError(err, "Extraktion fehlgeschlagen.", { aiContext: true }));
      }
    } finally {
      abortRef.current = null;
      setExtracting(false);
      setExtractStage(null);
      setPollDebug(null);
      setPollProgress(null);
      setDisplayProgress(null);
      setPollLabel("");
    }
  };

  // Iter 19.63.66 — Hinweis: `runAiReview` (LLM-Re-Evaluation) wurde durch
  // den deterministischen `runRecalcSum`-Pfad ersetzt. Eine vollständige
  // KI-Neuauswertung erfolgt jetzt explizit über „Eigene Änderungen
  // verwerfen" im Reset-Dialog → ruft `runAiExtract` mit den vorhandenen
  // Files erneut auf.

  const addManualRow = () => {
    userEditGenerationRef.current += 1;
    setRows([...rows, EmptyRow()]);
  };

  // Iter 19.63.103 — Cap-Bypass-Latch: nach jedem erfolgreichen Backend-
  // Recalc den letzten bekannten Cap-Status pinnen. So bleibt die Checkbox-
  // Sichtbarkeit bei den ANDEREN Zeilen stabil, während der User mehrere
  // Hakerl hintereinander setzt — und die KV-Regel-Erkennung bleibt aktiv,
  // auch wenn `backendAggregate` zwischenzeitlich invalidiert wurde.
  const updateCapBypassLatch = (result) => {
    if (!result) return;
    const capMonthsResult = result.cap_months || 0;
    const capDaysLimit = capMonthsResult * 30;
    const totalDays =
      typeof result.anrechenbare_dauer?.totalDays === "number"
        ? result.anrechenbare_dauer.totalDays
        : (result.anrechenbare_dauer?.jahre || 0) * 360 +
          (result.anrechenbare_dauer?.monate || 0) * 30 +
          (result.anrechenbare_dauer?.tage || 0);
    const bypassDays = result.anrechenbare_dauer_bypass?.totalDays || 0;
    const nonBypassDays = Math.max(0, totalDays - bypassDays);
    const preCapDays = result.anrechenbare_dauer_post_overlap_pre_cap?.totalDays || 0;
    const wasCapReached =
      capMonthsResult > 0 &&
      (preCapDays > totalDays + 5 || nonBypassDays >= capDaysLimit - 5);
    setLatchedCapBypassInfo({
      cap_bypass_rules: result.cap_bypass_rules || null,
      cap_months: capMonthsResult,
      was_cap_reached_at_last_recalc: wasCapReached,
    });
  };

  // Iter 19.63.66 / Runde 10 (button removed) — deterministische Math-
  // Aktualisierung der Beschäftigungszeiten-Summe basierend auf den aktuellen
  // (vom User eventuell editierten) Zeilen. Ruft KEIN LLM. Respektiert
  // Häkchen-Edits und Begründungen 1:1.
  //
  // Mockup→Live Phase 1: Optionale Parameter `signal` (AbortController) und
  // `silent` (kein Toast, kein Error-Banner) für Auto-Recalc-Hook. Bei
  // Abbruch durch neuen Trigger wird still returned.
  const runRecalcSum = async (opts = {}) => {
    const { signal, silent = false } = opts;
    if (rows.length === 0) {
      if (!silent) setError("Es sind keine Beschäftigungszeiten zum Berechnen vorhanden.");
      return;
    }
    if (!silent) setError("");
    setRecalculating(true);
    // Iter 20.03.P5-fix Runde 12 — Generations-Snapshot direkt VOR dem POST.
    // Wird nach der Response mit `userEditGenerationRef.current` verglichen.
    // Bei jeder User-Edit-Aktion (Toggle, Faktor, Datum, Add-Row, Bulk, …)
    // wird der Counter synchron hochgezählt. Wenn der Counter zwischen Send
    // und Receive steigt, verwerfen wir die gesamte Response — der aktuelle
    // Row-State bleibt unangetastet und der useDebouncedAutoRecalc-Hook
    // schedulet einen frischen Recalc mit den neuen User-Werten.
    const startGeneration = userEditGenerationRef.current;
    try {
      const payload = {
        kv_id: selectedKv.id,
        consent_ack: true,
        suggested_group: suggestedGroup || "",
        taetigkeit_kurz: taetigkeitKurz || "",
        stichtag: stichtag || "",
        rows: rows.map((r) => ({
          datum_von: r.datum_von || "",
          datum_bis: r.datum_bis || "",
          arbeitgeber: r.arbeitgeber || "",
          taetigkeit: r.taetigkeit || "",
          anrechenbar: Boolean(r.anrechenbar),
          anrechnungsfaktor:
            typeof r.anrechnungsfaktor === "number" ? r.anrechnungsfaktor : 1.0,
          anrechenbarkeit_begruendung: r.anrechenbarkeit_begruendung || "",
          was_laufend: Boolean(r.was_laufend),
          // Iter 19.63.101 — Cap-Bypass-Flag des Users (Checkbox in Step 3)
          // durchreichen. Backend `_apply_cap_to_items` überspringt diese
          // Items vom Cap-Walk → zählen über die Höchstanrechnungsdauer hinaus.
          cap_bypass: Boolean(r.cap_bypass),
          // Iter 20.36.f2 — WT-Kanzlei-Häkchen (Row-Checkbox in Step 3)
          // durchreichen. Backend `_apply_wt_kanzlei_faktor_reduktion`
          // entscheidet auf Basis dieses Flags, ob eine Zeile Non-WT ist
          // (dann halbiert wird) oder in den WT-Topf fällt (voll bleibt).
          // OHNE diese Zeile würde jedes Recalc `wt_kanzlei=false`
          // annehmen → WT-Zeilen fälschlich halbiert.
          wt_kanzlei: Boolean(r.wt_kanzlei),
          // Iter 20.36.f5 — Idempotenz-Marker der WT-Kanzlei-Kombiregel
          // durchreichen. Ohne diese Marker würde `_apply_wt_kanzlei_
          // faktor_reduktion` beim Recalc den Reset-Pfad überspringen
          // und den bereits reduzierten Faktor (0.5) NOCHMAL mit
          // `faktor_non_wt` multiplizieren → 0.25 → 0.125 (Stacking-Bug).
          _wt_non_kanzlei_reduced: Boolean(r._wt_non_kanzlei_reduced),
          _wt_previous_factor:
            typeof r._wt_previous_factor === "number"
              ? r._wt_previous_factor
              : null,
          // Iter 20.36.f3 — Idempotenz-Marker der Altersfaktor-Regel
          // durchreichen. Analog zum WT-Fall: ohne die Marker würde
          // die Regel beim Recalc erneut halbieren.
          _altersfaktor_applied: Boolean(r._altersfaktor_applied),
          _altersfaktor_previous_factor:
            typeof r._altersfaktor_previous_factor === "number"
              ? r._altersfaktor_previous_factor
              : null,
          // Iter 20.36.f14 — Split-Meta durchreichen. Der Backend-Reverter
          // liest diesen Marker und resetiert `months_anrechenbar` beim
          // nächsten Recalc.
          _altersfaktor_split:
            r._altersfaktor_split && typeof r._altersfaktor_split === "object"
              ? r._altersfaktor_split
              : null,
          // Iter 19.68.8 — Konsolidierungs-Lücken-Tage über Roundtrip
          // erhalten. Wird beim initialen Extract vom Backend gesetzt
          // (`_deterministic_consolidate`). Ohne diese Durchreichung würde
          // der Backend-Recalc gap_days=0 annehmen und die Lücke wieder
          // als Beschäftigungstag mitzählen (= „4 T."-Bug aus Iter 19.68.6).
          _consolidation_gap_days:
            typeof r._consolidation_gap_days === "number"
              ? r._consolidation_gap_days
              : 0,
          // Iter 20.16 — KV-Praktikum-Auto-Ausschluss-Marker über Roundtrip
          // erhalten. Wird beim initialen Extract vom Backend gesetzt
          // (`_apply_kv_praktikum_ausschluss`). Ohne diese Durchreichung
          // würde die Recalc-Response den Marker verlieren → Frontend
          // zeigt bei folgenden Interaktionen wieder die Choice-Buttons.
          _kv_praktika_ausgeschlossen: Boolean(r._kv_praktika_ausgeschlossen),
        })),
      };
      const { data } = await api.post(
        "/tool/recalc-vordienstzeiten",
        payload,
        { timeout: 20_000, signal },
      );
      const result = data?.result;
      if (!result) throw new Error("Unerwartete Antwort.");
      // Iter 20.03.P5-fix Runde 12 — Race-Protection via Generations-Counter.
      // Wenn der User während des Roundtrips irgendeine Row-Änderung gemacht
      // hat (Toggle, Faktor, Datum, …), verwerfen wir die gesamte Response.
      // Der User-State bleibt unangetastet; `setBackendAggregate(null)`
      // hält isDirty=true, sodass der useDebouncedAutoRecalc-Hook direkt
      // einen frischen Recalc mit den neuen Werten schedulet.
      // Vorteil gegenüber dem alten pro-Row-drift-Check: funktioniert
      // unabhängig davon, ob React 18 die setRows-Updater synchron aufruft.
      if (userEditGenerationRef.current !== startGeneration) {
        if (typeof setBackendAggregate === "function") setBackendAggregate(null);
        return;
      }
      const backendItems = result.items || [];
      setRows((prevRows) =>
        backendItems.map((it, idx) => mapBackendItem(it, prevRows[idx] || {})),
      );
      if (typeof setBackendAggregate === "function" && result.anrechenbare_dauer) {
        setBackendAggregate({
          display: result.anrechenbare_dauer.display || "",
          jahre: result.anrechenbare_dauer.jahre || 0,
          monate: result.anrechenbare_dauer.monate || 0,
          tage: result.anrechenbare_dauer.tage || 0,
          totalDays:
            typeof result.anrechenbare_dauer.totalDays === "number"
              ? result.anrechenbare_dauer.totalDays
              : (result.anrechenbare_dauer.jahre || 0) * 360 +
                (result.anrechenbare_dauer.monate || 0) * 30 +
                (result.anrechenbare_dauer.tage || 0),
          roh: result.anrechenbare_dauer_roh
            ? {
                display: result.anrechenbare_dauer_roh.display || "",
                totalDays: result.anrechenbare_dauer_roh.totalDays || 0,
              }
            : null,
          post_overlap_pre_cap: result.anrechenbare_dauer_post_overlap_pre_cap
            ? {
                display:
                  result.anrechenbare_dauer_post_overlap_pre_cap.display || "",
                totalDays:
                  result.anrechenbare_dauer_post_overlap_pre_cap.totalDays || 0,
              }
            : null,
          exceeded:
            (result.cap_months || 0) > 0 &&
            (result.anrechenbare_dauer_post_overlap_pre_cap?.totalDays ||
              result.anrechenbare_dauer_roh?.totalDays ||
              0) >
              (result.anrechenbare_dauer.jahre * 360 +
                result.anrechenbare_dauer.monate * 30 +
                result.anrechenbare_dauer.tage) +
                5,
          // Iter 19.63.101 — Cap-Bypass-Aggregat + Regel-Konfiguration.
          // Frontend braucht beides: Aggregat für die Sidebar-Anzeige
          // („+ X J. Y M. Selber AG"), Regel für die Checkbox-Sichtbarkeit
          // (nur einblenden wenn `cap_bypass_rules.same_employer != null`).
          bypass: result.anrechenbare_dauer_bypass
            ? {
                display: result.anrechenbare_dauer_bypass.display || "",
                jahre: result.anrechenbare_dauer_bypass.jahre || 0,
                monate: result.anrechenbare_dauer_bypass.monate || 0,
                tage: result.anrechenbare_dauer_bypass.tage || 0,
                totalDays: result.anrechenbare_dauer_bypass.totalDays || 0,
              }
            : null,
          cap_bypass_rules: result.cap_bypass_rules || null,
          cap_months: result.cap_months || 0,
        });
      }
      // Iter 19.63.103 — Latch nach erfolgreichem deterministischem Recalc.
      updateCapBypassLatch(result);
      // Hinweise (z.B. Prüfungsfilter-Drop, ungültige Datums) durchreichen,
      // aber Zusammenfassung NICHT überschreiben — das ist KI-Output, der
      // bei rein deterministischer Summen-Aktualisierung unangetastet
      // bleiben soll.
      setAiInfo((prev) => ({
        zusammenfassung: prev?.zusammenfassung || "",
        hinweise: result.hinweise && result.hinweise.length > 0
          ? result.hinweise
          : (prev?.hinweise || []),
        anrechenbare_monate: result.anrechenbare_monate || 0,
        quellenqualitaet: prev?.quellenqualitaet ?? null,
        count: backendItems.length,
        consolidation: prev?.consolidation || null,
      }));
      toast.success("Summe aktualisiert (deterministisch, ohne erneute Auswertung).", {
        // Bei Auto-Recalc keinen Toast anzeigen — würde sonst bei jedem
        // Klick auf ANR-Switch oder Faktor-Änderung erscheinen (störend).
        // Der silent-Mode wird vom Debounced-Hook verwendet.
        ...(silent ? { duration: 0 } : {}),
      });
      if (silent) toast.dismiss();
    } catch (err) {
      // Abbruch durch neuen Trigger (AbortController) → still returnen,
      // KEIN Fehler-Banner. Axios v1: err.code === "ERR_CANCELED"; Fetch:
      // err.name === "AbortError".
      const isAborted =
        err?.code === "ERR_CANCELED" ||
        err?.name === "AbortError" ||
        err?.name === "CanceledError";
      if (isAborted) {
        return;
      }
      if (!silent) {
        setError(extractError(err, "Summen-Aktualisierung fehlgeschlagen."));
      } else {
        // Bei silent (Auto-Recalc) Fehler zumindest in der Konsole loggen,
        // damit Support-Fälle nachvollziehbar bleiben.
        // eslint-disable-next-line no-console
        console.warn("[auto-recalc] silent fail:", err?.message || err);
      }
    } finally {
      setRecalculating(false);
    }
  };

  // Iter 19.63.66 — Soft Reset: Eigene Änderungen verwerfen.
  // Behält die hochgeladenen Dokumente, ruft die KI-Extraktion erneut auf.
  // User-Edits werden durch frische LLM-Ergebnisse überschrieben.
  const handleSoftReset = async () => {
    if (files.length === 0) {
      toast.error(
        "Keine Dokumente zum erneuten Auswerten vorhanden. Bitte „Alles verwerfen“ wählen.",
      );
      return;
    }
    setResetDialogOpen(false);
    // Zustand für eine frische Extraktion vorbereiten — Zeilen leeren,
    // Aggregate verwerfen, dann KI erneut auf den vorhandenen Files laufen
    // lassen. `runAiExtract` setzt den Rest selbst.
    userEditGenerationRef.current += 1;
    setRows([]);
    if (typeof setBackendAggregate === "function") setBackendAggregate(null);
    setAiInfo(null);
    // Hinweis: setAusbildungszeiten([]) absichtlich NICHT — bei Soft-Reset
    // bleiben Ausbildungs-Bonuszeiten erhalten (sie sind nicht durch
    // User-Edits in der Tabelle entstanden, sondern eigenständig).
    await runAiExtract();
  };

  // Iter 19.63.66 — Hard Reset: Alles verwerfen, Step 3 von vorne beginnen.
  // Verwirft Files, Zeilen, Ausbildungs-Bonuszeiten, KI-Info und Aggregate.
  const handleHardReset = () => {
    setResetDialogOpen(false);
    // Blob-URLs der Preview-Files freigeben (sonst RAM-Leak im Browser).
    for (const b of sourceBlobs) {
      try { URL.revokeObjectURL(b.url); } catch (_e) { /* noop */ }
    }
    userEditGenerationRef.current += 1;
    setFiles([]);
    setSourceBlobs([]);
    setRows([]);
    if (typeof setAusbildungszeiten === "function") setAusbildungszeiten([]);
    if (typeof setBackendAggregate === "function") setBackendAggregate(null);
    setAiInfo(null);
    setError("");
    setUploadCollapsed(false);
    if (inputRef.current) inputRef.current.value = "";
    toast.info("Schritt 3 wurde komplett zurückgesetzt.");
  };

  // Audit-Fix P2 — updateRow/removeRow sind jetzt stabile useCallback-Refs
  // mit functional setState, damit React.memo(VordienstRow) greift und ein
  // Edit in einer Zeile nicht alle N Zeilen re-rendert (Signatur: (idx, next)).
  const updateRow = useCallback((idx, incoming) => {
    userEditGenerationRef.current += 1;
    return setRows((prevRows) => {
    let next = incoming;
    // Iter 19.31: When the tätigkeit changes, auto-apply the KV's factor
    // rules. This is the SAME mapping the backend uses, so the UI stays
    // consistent without needing a roundtrip. Only kicks in if the rule
    // hasn't been manually adjusted by the user (we detect that by checking
    // whether the existing begründung already contains our rule marker).
    const prev = prevRows[idx];
    const taetigkeitChanged = prev && prev.taetigkeit !== next.taetigkeit;
    // Iter 19.63.93 — Praktikum-Typ-Wahl ist KEIN manueller Tätigkeits-Edit.
    // `setPraktikumTyp()` (shared.js) ändert die Tätigkeit deterministisch
    // von „Pflichtpraktikum …" zu „Angestellter — Pflichtpraktikum …" und
    // bewahrt absichtlich die original-LLM-Begründung (Iter 86 Fix).
    // Ohne diese Erkennung würde der Auto-Rule-Pfad unten die Begründung
    // mit dem technischen Fallback-Text „[Faktor 1 gesetzt gemäß KV-Regel
    // …]" überschreiben. Bug-Report Praktikum-Begründungs-Verlust nach
    // „Angestellter"-Klick: Iter 91-Test-Screenshot.
    //
    // Die Praktikum-Typ-Wahl-Logik in `setPraktikumTyp` setzt bereits
    // Faktor + Anrechenbar korrekt; Badge („ANGESTELLTER"/„ARBEITER") +
    // Status-Zeile („✓ Praktikum als X eingestuft") kommunizieren die
    // Wahl klar an den User. Begründung bleibt erhalten.
    const praktikumTypChanged =
      prev &&
      prev.praktikum_anstellungstyp !== next.praktikum_anstellungstyp;
    if (
      taetigkeitChanged &&
      selectedKv?.faktor_regeln &&
      !praktikumTypChanged &&
      prev._factor_manual !== true
    ) {
      const rule = applyKvFactorRule(next.taetigkeit, selectedKv.faktor_regeln);
      if (rule) {
        next = {
          ...next,
          anrechenbar: rule.anrechenbar,
          anrechnungsfaktor: rule.faktor,
          anrechenbarkeit_begruendung: rule.begruendung,
          // Iter 19.31 / 19.63.67 — Alle Backend-Cache-Felder löschen, damit
          // VordienstRow `monthsEffective = months × faktor` neu rechnet und
          // nicht an einem stalen 0-Wert hängen bleibt (z.B. nach „Summe
          // aktualisieren" mit anrechenbar=false). Liste muss synchron sein
          // mit dem relevantChanged-Branch weiter unten.
          months_anrechenbar: undefined,
          effective_days_anrechenbar: null,
          dauer_anrechenbar_jahre: undefined,
          dauer_anrechenbar_monate: undefined,
          dauer_anrechenbar_tage: undefined,
          display_raw: undefined,
          display_anrechenbar: undefined,
          effective_period: undefined,
          display_anrechenbar_pre_cap: undefined,
          dauer_anrechenbar_pre_cap_jahre: undefined,
          dauer_anrechenbar_pre_cap_monate: undefined,
          dauer_anrechenbar_pre_cap_tage: undefined,
        };
      } else if (
        prev.anrechenbarkeit_begruendung &&
        prev.anrechenbarkeit_begruendung.startsWith("[Faktor ")
      ) {
        // Previous value came from an auto-rule; new tätigkeit doesn't match
        // any rule → clear the auto-applied begründung so user knows it's
        // their turn to evaluate or run "KI prüfen".
        next = {
          ...next,
          anrechenbarkeit_begruendung: "",
          months_anrechenbar: undefined,
          effective_days_anrechenbar: null,
          dauer_anrechenbar_jahre: undefined,
          dauer_anrechenbar_monate: undefined,
          dauer_anrechenbar_tage: undefined,
          display_raw: undefined,
          display_anrechenbar: undefined,
          effective_period: undefined,
          display_anrechenbar_pre_cap: undefined,
          dauer_anrechenbar_pre_cap_jahre: undefined,
          dauer_anrechenbar_pre_cap_monate: undefined,
          dauer_anrechenbar_pre_cap_tage: undefined,
        };
      }
    }
    // Iter 19.42.3 / 19.63.67 — Wenn relevante Felder geändert wurden, ALLE
    // backend-autoritativen Per-Row-Cache-Felder invalidieren, damit der
    // VordienstRow-Fallback (`months × faktor`) die korrekten Werte berechnet
    // bis der nächste Auto-Recalc-Roundtrip anschlägt.
    //
    // BUG-Geschichte (Iter 19.63.67):
    // - Iter 19.42.3 invalidierte nur `effective_days_anrechenbar`.
    // - Nach dem Recalc-Roundtrip mit anrechenbar=false hatte das Backend
    //   die Felder `months_anrechenbar`, `dauer_anrechenbar_*`, `display_*`,
    //   `effective_period` korrekt auf 0 gesetzt.
    // - Beim Re-Aktivieren des ANR-Häkchens blieben diese 0-Werte stehen.
    // - VordienstRow priorisiert Backend-Werte (Math.min, hasBackendDays,
    //   hasBackendDisplay) → Zeile bleibt „0 T." durchgestrichen, obwohl
    //   der User wieder anrechenbar=true gesetzt hat.
    //
    // Fix: ALLE Cache-Felder zugleich invalidieren. Der Auto-Recalc-Pulse-
    // Indicator neben der KPI zeigt dem User an, dass gerade neu berechnet
    // wird — kein manuelles Anstoßen mehr nötig.
    if (prev) {
      const relevantChanged =
        prev.datum_von !== next.datum_von ||
        prev.datum_bis !== next.datum_bis ||
        prev.anrechenbar !== next.anrechenbar ||
        prev.anrechnungsfaktor !== next.anrechnungsfaktor;
      if (relevantChanged) {
        next = {
          ...next,
          months_anrechenbar: undefined,
          effective_days_anrechenbar: null,
          dauer_anrechenbar_jahre: undefined,
          dauer_anrechenbar_monate: undefined,
          dauer_anrechenbar_tage: undefined,
          display_raw: undefined,
          display_anrechenbar: undefined,
          effective_period: undefined,
          // Pre-Cap-Display-Varianten ebenfalls löschen, falls vorhanden.
          display_anrechenbar_pre_cap: undefined,
          dauer_anrechenbar_pre_cap_jahre: undefined,
          dauer_anrechenbar_pre_cap_monate: undefined,
          dauer_anrechenbar_pre_cap_tage: undefined,
        };
      }
    }
    return prevRows.map((r, i) => (i === idx ? next : r));
    });
  }, [setRows, selectedKv]);
  const removeRow = useCallback((idx) => {
    userEditGenerationRef.current += 1;
    setRows((prevRows) => prevRows.filter((_, i) => i !== idx));
  }, [setRows]);

  const totalSize = files.reduce((s, f) => s + f.size, 0);
  const capMonths = selectedKv?.cap_months || 0;
  // Iter 19.42.1 — Hybrid-Aggregation: Faktor=1.0 ohne Verlust → Y/M/T direkt
  // (kein Tage-Drift); sonst Tage-Pfad mit Faktor × (J×365 + M×30 + T).
  // Phase 3 (User-Original-Spec): /30 Tage→Monate, /12 Monate→Jahre.
  // Iter 19.43.7 — Dirty-State-Erkennung. Wenn der Backend-autoritative
  // Aggregate-Wert NULL ist (= seit dem letzten KI-Check vom User editiert),
  // sind alle errechneten Summen "stale". Wir zeigen dann "Neuberechnung
  // nötig" statt eines Approximations-Werts, der den User verwirrt.
  const isDirty = rows.length > 0 && !backendAggregate;

  // ================================================================
  // Mockup→Live Phase 1 — Debounced Auto-Recalc
  // ----------------------------------------------------------------
  // Ersetzt (bei aktivem Feature-Flag) den manuellen „Summe
  // aktualisieren"-Button durch einen automatischen Aufruf 500 ms nach
  // der letzten Änderung. Backend bleibt Source of Truth.
  //
  // Skip-Bedingungen (paused):
  //  - während laufendem KI-Extract (extracting/reviewing)
  //  - während bereits laufender manueller Recalc-Anfrage
  //  - beim allerersten Row-Set-Change (Extract-Ergebnis)
  // ================================================================
  const autoRecalcEnabled = isFeatureEnabled("step3AutoRecalc");
  // Mockup→Live Phase 2 — Kompakt-/Detail-Density-Toggle.
  // Sichtbar nur wenn das Feature-Flag aktiv ist. In Preview default „Kompakt",
  // in Produktion default „Detail" (das aktuelle Verhalten). Wahl wird pro
  // Browser via localStorage persistiert, damit Sachbearbeiter sich einmal
  // entscheiden und die Wahl über Sitzungen behalten.
  const compactRowEnabled = isFeatureEnabled("step3CompactRow");
  const timelineEnabled = isFeatureEnabled("step3Timeline");
  const bulkActionsEnabled = isFeatureEnabled("step3BulkActions");
  // Mockup→Live Phase 5 — Bulk-Actions-Selection.
  // Selektierte Zeilen-Indices (bezüglich der `rows`-Array-Reihenfolge,
  // NICHT bezüglich gefilterter Ansicht). Nach dem Anwenden einer Bulk-
  // Aktion bleiben die Zeilen ausgewählt, damit der User weitere Aktionen
  // anwenden kann; „Auswahl aufheben" leert das Set explizit.
  const [selectedIndices, setSelectedIndices] = useState(() => new Set());
  const handleSelectToggle = useCallback((idx) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);
  const handleClearSelection = useCallback(() => {
    setSelectedIndices(new Set());
  }, []);
  // Bulk-Action-Handler.
  //   • set_anrechenbar → row.anrechenbar = params.anrechenbar (bei true
  //     und faktor=0 wird faktor auf 1.0 gesetzt, analog Kompakt-ANR-Switch)
  //   • set_faktor    → row.anrechnungsfaktor = params.faktor + _factor_manual=true
  //   • mark_reviewed → row._quality_ack = true (löscht offene Auslese-Warnungen)
  // Aktionen laufen als ein einziger setRows-Call → nur ein React-Re-Render
  // und ein Auto-Recalc-Trigger.
  const handleBulkApply = useCallback((actionKey, params) => {
    userEditGenerationRef.current += 1;
    setRows((prev) => prev.map((r, i) => {
      if (!selectedIndices.has(i)) return r;
      if (actionKey === "set_anrechenbar") {
        const next = { ...r, anrechenbar: !!params.anrechenbar };
        if (params.anrechenbar && (!r.anrechnungsfaktor || r.anrechnungsfaktor === 0)) {
          next.anrechnungsfaktor = 1.0;
        }
        // Backend-Werte invalidieren, damit Recalc greift
        return {
          ...next,
          months_anrechenbar: undefined,
          effective_days_anrechenbar: undefined,
          display_anrechenbar: undefined,
        };
      }
      if (actionKey === "set_faktor") {
        const faktor = Math.max(0, Math.min(1, Number(params.faktor) || 0));
        return {
          ...r,
          anrechnungsfaktor: faktor,
          _factor_manual: true,
          months_anrechenbar: undefined,
          effective_days_anrechenbar: undefined,
          effective_period: undefined,
          display_anrechenbar: undefined,
          display_anrechenbar_pre_cap: undefined,
          _cap_reduced: undefined,
          _cap_zeroed: undefined,
        };
      }
      if (actionKey === "mark_reviewed") {
        return { ...r, _quality_ack: true };
      }
      return r;
    }));
    // Bei „mark_reviewed" wird die Auto-Recalc-Kette nicht getriggert
    // (Werte ändern sich rechnerisch nicht), aber die Zeilen-Warnungen
    // verschwinden.
  }, [selectedIndices, setRows]);
  const [showTimeline, setShowTimeline] = useState(() => {
    if (!timelineEnabled) return false;
    try {
      const stored = window.localStorage.getItem("et.step3.timeline");
      if (stored === "1") return true;
      if (stored === "0") return false;
    } catch (_e) { /* noop */ }
    return false; // Default: geschlossen, um KPI-Panel nicht zu überladen
  });
  const toggleTimeline = useCallback(() => {
    setShowTimeline((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem("et.step3.timeline", next ? "1" : "0");
      } catch (_e) { /* noop */ }
      return next;
    });
  }, []);
  const [compactMode, setCompactMode] = useState(() => {
    if (!compactRowEnabled) return false;
    try {
      const stored = window.localStorage.getItem("et.step3.density");
      if (stored === "compact") return true;
      if (stored === "detail") return false;
    } catch (_e) { /* noop */ }
    // Kein persistierter Wert → Kompakt-Modus als neue Default-Ansicht,
    // damit User beim ersten Öffnen den neuen Zustand sehen (kann mit
    // einem Klick auf Detail zurückgesetzt werden).
    return true;
  });
  const toggleCompactMode = useCallback(() => {
    setCompactMode((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem("et.step3.density", next ? "compact" : "detail");
      } catch (_e) { /* noop */ }
      return next;
    });
  }, []);
  const runAutoRecalc = useCallback(
    async (_seq, signal) => {
      await runRecalcSum({ signal, silent: true });
    },
    // runRecalcSum ist keine memoizierte Referenz und würde bei jeder
    // Render-Iteration eine neue Identity haben → würde den Hook in eine
    // Endlosschleife treiben. Wir stützen uns bewusst auf die
    // primitiven Werte, die die Logik beeinflussen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, selectedKv?.id, suggestedGroup, taetigkeitKurz, stichtag],
  );
  const autoRecalc = useDebouncedAutoRecalc({
    enabled: autoRecalcEnabled,
    trigger: rows,
    isDirty,
    paused: extracting || reviewing || recalculating,
    runRecalc: runAutoRecalc,
    delay: 500,
  });

  // Iter 19.59 — Frontend-Math wird ausschließlich für die Roh-Gesamt-Anzeige
  // (vor Cap) und für Edit-Time-Plausibilitätschecks verwendet. Die
  // AUTORITATIVE "Anrechenbar gesamt"-Anzeige in der Sidebar kommt direkt
  // aus `backendAggregate` — Single Source of Truth, identisch zur PDF und
  // zur Empfehlung in Step 4.
  // Iter 19.63.28 — Frontend rechnet KEINE Aggregate mehr. Alles kommt vom
  // Backend (`backendAggregate.display`, `.roh.display`, `.exceeded`).
  // Fallback bei leerem Backend-Aggregat: neutraler 0-State, damit die
  // Sidebar nicht crashed (User landet z.B. ohne Step-3-Daten auf der Seite).
  // Iter 20.03.P5-fix Runde 13 — Letzter bekannter Wert wird im Ref
  // gepinnt, sodass beim dirty-Zustand nicht der breite Text „Neuberechnung
  // nötig" die Kachel verzerrt. Der Sky-Dot signalisiert die Aktivität.
  if (backendAggregate && backendAggregate.display) {
    lastKnownDisplayRef.current = backendAggregate.display;
  }
  const authoritativeAggregate = backendAggregate && backendAggregate.display
    ? {
        jahre: backendAggregate.jahre ?? 0,
        monate: backendAggregate.monate ?? 0,
        tage: backendAggregate.tage ?? 0,
        totalDays: backendAggregate.totalDays ?? 0,
        display: backendAggregate.display,
        exceeded: backendAggregate.exceeded ?? false,
      }
    : {
        jahre: 0, monate: 0, tage: 0, totalDays: 0,
        display: rows.length > 0
          ? (lastKnownDisplayRef.current || "0 T.")
          : "0 T.",
        exceeded: false,
      };
  // Iter 19.63.35 — Sidebar-„Gesamt"-Summe nutzt jetzt Post-Overlap-Pre-Cap
  // (Backend liefert `backendAggregate.post_overlap_pre_cap.display`),
  // damit die User-Erwartung (Überlappungen sichtbar reduziert, Cap noch nicht
  // angewendet) erfüllt wird. Fallback: `.roh` (Pre-Cap, Pre-Overlap) für
  // Backwards-Compat, falls Backend das Feld noch nicht liefert.
  const aggregateRoh = backendAggregate?.post_overlap_pre_cap
    ? {
        display: backendAggregate.post_overlap_pre_cap.display || "",
        totalDays: backendAggregate.post_overlap_pre_cap.totalDays || 0,
      }
    : backendAggregate?.roh
      ? {
          display: backendAggregate.roh.display || "",
          totalDays: backendAggregate.roh.totalDays || 0,
        }
      : {
          display: "",
          totalDays: 0,
        };
  // Legacy-Aliases entfernt (Iter 19.63.28) — alle Verbraucher nutzen
  // jetzt direkt `authoritativeAggregate.exceeded` und `aggregateRoh.totalDays`.
  const capYears = capMonths ? Math.round(capMonths / 12) : 0;

  // Iter 19.63.101 — Cap-Bypass-Aggregat (Σ aller _cap_bypassed Items).
  // Wird bereits in `backendAggregate.bypass.totalDays` aus dem Backend
  // geliefert. Fallback: 0.
  const bypassVordienstAggregate = backendAggregate?.bypass
    ? {
        display: backendAggregate.bypass.display || "",
        totalDays: backendAggregate.bypass.totalDays || 0,
      }
    : { display: "", totalDays: 0 };
  // Cap-Bypass-Regeln aus dem KV (None, wenn der KV diese Sonderregel nicht hat).
  // Frontend nutzt das, um die „Selber AG (Bypass)"-Checkbox conditional
  // einzublenden — wird über `VordienstRow.capBypassRules` weitergereicht.
  //
  // Iter 19.63.103 — LATCHING: `backendAggregate` wird durch jeden Row-Edit
  // via `setVordienstzeitenWithInvalidate` auf null gesetzt. Damit
  // verschwand vorher die Checkbox sofort nach dem ersten Klick. Fix:
  // `latchedCapBypassInfo` cached die KV-Regel + Cap-Reached-Marker aus
  // dem letzten erfolgreichen Recalc und liefert sie als Fallback.
  const capBypassRules =
    backendAggregate?.cap_bypass_rules || latchedCapBypassInfo?.cap_bypass_rules || null;
  // isCapReached: Wenn die anrechenbaren NICHT-Bypass-Items den Cap
  // bereits ausschöpfen. Wir bevorzugen das frische Backend-Aggregat (für
  // den Initial-State direkt nach KI-Extract), fallen aber bei Dirty-State
  // auf den gelatchten Wert vom letzten Recalc zurück — sodass die Checkbox
  // beim Setzen weiterer Hakerl nicht flackert.
  const capDaysLimit = capMonths * 30;
  const nonBypassAnrechenbarDays = Math.max(
    0,
    (authoritativeAggregate.totalDays || 0) - (bypassVordienstAggregate.totalDays || 0),
  );
  const isCapReachedLive =
    capMonths > 0 &&
    (authoritativeAggregate.exceeded ||
      nonBypassAnrechenbarDays >= capDaysLimit - 5);
  const isCapReached = backendAggregate
    ? isCapReachedLive
    : Boolean(latchedCapBypassInfo?.was_cap_reached_at_last_recalc);

  // Iter 19.63.10 / 19.63.37 / 19.63.38 — Effective Total inkl. Ausbildungs-Bonuszeiten.
  //
  // Math zentralisiert in `computeEffectiveTotal()` (Iter 19.63.38) —
  // siehe shared.js für die Math-Spec. Backend-Mirror in
  // `vordienstzeiten.compute_effective_total_days()`.
  const _eff = computeEffectiveTotal({
    vordienstDays: authoritativeAggregate?.totalDays || 0,
    ausbildungszeiten,
    capMonths,
  });
  const effectiveCapMonths = capMonths + _eff.bypassMonths;
  const effectiveCapYears = effectiveCapMonths ? Math.round(effectiveCapMonths / 12) : 0;

  const bypassMonthsActive = _eff.bypassMonths;
  const bypassDaysActive = _eff.bypassMonths * 30;
  const normalBonusMonthsActive = _eff.normalBonusMonths;
  const hasAnyBonus = _eff.normalBonusMonths > 0 || _eff.bypassMonths > 0;
  const finalAggregate = hasAnyBonus
    ? {
        jahre: _eff.jahre,
        monate: _eff.monate,
        tage: _eff.tage,
        totalDays: _eff.effectiveTotalDays,
        display: _eff.display,
        exceeded: authoritativeAggregate?.exceeded || _eff.exceeded,
      }
    : authoritativeAggregate;

  return (
    <section
      className="border border-ink-200 bg-white rounded-sm relative overflow-hidden"
      data-testid="tool-step-3"
    >
      <div className="absolute top-0 left-0 h-1 w-24 bg-brand-400" aria-hidden="true" />

      <div className="px-8 py-8 border-b border-ink-200">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm border border-ink-200 bg-ink-50">
            <ClockCounterClockwise
              size={22}
              weight="duotone"
              className="text-brand-500"
            />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
              Schritt 3 von 4
            </div>
            <h2 className="font-cabinet text-2xl tracking-tight text-ink-900 font-medium leading-tight">
              Vordienstzeiten erfassen
            </h2>
            <p className="mt-2 text-sm text-warm-700 leading-relaxed">
              Laden Sie einen <strong>Versicherungsdatenauszug</strong> (SV-Auszug),
              den <strong>Lebenslauf</strong> oder <strong>Dienstzeugnisse</strong>{" "}
              hoch — ET extrahiert alle Beschäftigungszeiten und schlägt eine{" "}
              <strong>Anrechenbarkeit</strong> auf die Einstufung vor. Sie können
              jede Zeile manuell prüfen und anpassen.
            </p>
          </div>
          <HelpAnchor
            anchor="schritt-3"
            label="Hilfe zu Schritt 3 — Vordienstzeiten prüfen"
            testId="tool-step-3-help"
            className="shrink-0 mt-1"
          />
        </div>
      </div>

      <div className="px-8 py-7 space-y-7">
        {stichtag && (() => {
          const stichtagDate = new Date(stichtag);
          const stichtagMinusOne = new Date(stichtagDate);
          stichtagMinusOne.setDate(stichtagMinusOne.getDate() - 1);
          const fmt = (d) => d.toLocaleDateString("de-AT", {
            day: "2-digit", month: "long", year: "numeric",
          });
          return (
            <div
              data-testid="tool-step3-stichtag-info"
              className="flex items-start gap-2 border border-ink-200 bg-warm-50 rounded-sm px-4 py-2.5 text-[12px] text-warm-700 leading-snug"
            >
              <span className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 shrink-0 mt-0.5">
                Stichtag
              </span>
              <span>
                <span className="font-medium text-ink-900">{fmt(stichtagDate)}</span>
                <span className="text-warm-500">
                  {" "}— „laufende&#8220; Beschäftigungen werden bis{" "}
                  <span className="font-medium text-ink-900">{fmt(stichtagMinusOne)}</span>{" "}
                  (1 Tag vor dem Stichtag) gerechnet, da der Stichtag selbst bereits der erste Tag im neuen Job ist.
                </span>
              </span>
            </div>
          );
        })()}

        <div className="relative space-y-7">
          {(extracting || reviewing) && (
            <AiBusyOverlay
              label={
                extracting
                  ? extractStage === "refine"
                    ? "ET prüft die Anrechenbarkeit …"
                    : "ET liest die Dokumente …"
                  : "ET prüft die Anrechenbarkeit …"
              }
              hint={
                extracting && pollLabel
                  ? pollLabel.replace(/\s*\(\d+\s*s\)\s*$/, "")
                  : "Verarbeitung läuft"
              }
              progress={extracting ? displayProgress : null}
              testId="tool-step3-busy-overlay"
              onCancel={cancelAiBusy}
            />
          )}

          <FileUploadPane
            inputRef={inputRef}
            files={files}
            totalSize={totalSize}
            error={error}
            extracting={extracting}
            reviewing={reviewing || recalculating}
            rowsCount={rows.length}
            onFilePick={onFilePick}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onRemoveFile={removeFile}
            onExtract={runAiExtract}
            onAddManual={addManualRow}
            collapsed={uploadCollapsed && files.length > 0}
            onToggleCollapsed={() => setUploadCollapsed((v) => !v)}
            dirty={isDirty}
          />

          {aiInfo && (
            <div
              data-testid="tool-step3-ai-info"
              className="border border-sky-200 bg-sky-50/50 rounded-sm p-4 text-sm"
            >
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-sky-700">
                  Zusammenfassung
                </div>
                {sourceBlobs.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setCompareOpen(true)}
                    data-testid="tool-step3-compare-open"
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-700 hover:text-brand-600 transition-crisp"
                    title="Quelle und Extraktion nebeneinander prüfen"
                  >
                    <Eye size={13} weight="bold" />
                    Quelle vs. Extraktion
                  </button>
                )}
              </div>
              <div className="text-ink-900 leading-relaxed">
                {aiInfo.zusammenfassung ||
                  `${aiInfo.count} Beschäftigungsverhältnis${aiInfo.count === 1 ? "" : "se"} extrahiert.`}
              </div>
              {aiInfo.hinweise?.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {aiInfo.hinweise.map((h, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-[12px] text-ink-700"
                      data-testid={`tool-step3-hinweis-${i}`}
                    >
                      <Info size={13} weight="bold" className="mt-0.5 shrink-0 text-sky-500" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {aiInfo?.quellenqualitaet?.score != null && aiInfo.quellenqualitaet.score < 70 && (
            <QualityBanner
              quality={aiInfo.quellenqualitaet}
              onOpenCompare={sourceBlobs.length > 0 ? () => setCompareOpen(true) : null}
            />
          )}

          {/* Editable table */}
          <div data-testid="tool-step3-table-wrap">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
              <div className="flex items-center gap-2">
                <Briefcase size={16} weight="duotone" className="text-brand-500" />
                <Label className="mb-0">Beschäftigungszeiten</Label>
                {/* Mockup→Live Phase 2 — Density-Toggle (Kompakt/Detail).
                    Nur sichtbar wenn Feature-Flag aktiv ist. Speichert Wahl
                    in localStorage. */}
                {compactRowEnabled && rows.length > 0 && (
                  <div
                    className="ml-3 inline-flex items-center rounded-sm border border-ink-200 bg-white p-0.5"
                    data-testid="tool-step3-density-toggle"
                    role="group"
                    aria-label="Zeilen-Dichte umschalten"
                  >
                    <button
                      type="button"
                      onClick={() => { if (compactMode) toggleCompactMode(); }}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-sm px-2 py-1 text-[11px] font-medium transition-crisp",
                        !compactMode
                          ? "bg-ink-100 text-ink-900"
                          : "text-ink-500 hover:text-ink-900 hover:bg-ink-50",
                      )}
                      aria-pressed={!compactMode}
                      data-testid="tool-step3-density-detail"
                      title="Detail-Ansicht — alle Felder direkt sichtbar"
                    >
                      <Rows size={12} weight="bold" />
                      <span>Detail</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { if (!compactMode) toggleCompactMode(); }}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-sm px-2 py-1 text-[11px] font-medium transition-crisp",
                        compactMode
                          ? "bg-ink-100 text-ink-900"
                          : "text-ink-500 hover:text-ink-900 hover:bg-ink-50",
                      )}
                      aria-pressed={compactMode}
                      data-testid="tool-step3-density-compact"
                      title="Kompakt-Ansicht — je Zeile ~44 px, Details via Chevron"
                    >
                      <List size={12} weight="bold" />
                      <span>Kompakt</span>
                    </button>
                  </div>
                )}
                {/* Mockup→Live Phase 5 / Iter 20.03.P5-fix Runde 7 —
                    „Auswahl-Modus"-Umschalter. Ehemals nackte Checkbox
                    mit dem Label „Alle auswählen" — nach UX-Audit P1:
                    als Text-Ghost-Button mit klarerer Semantik gerendert,
                    damit User die mächtige Bulk-Aktion überhaupt entdecken.
                    Klick startet den Auswahl-Modus (setzt alle Zeilen)
                    oder verlässt ihn (leert Selektion). Fortschrittsanzeige
                    im Label. */}
                {bulkActionsEnabled && compactRowEnabled && compactMode && rows.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedIndices.size > 0) {
                        setSelectedIndices(new Set());
                      } else {
                        setSelectedIndices(new Set(rows.map((_, i) => i)));
                      }
                    }}
                    className={cn(
                      "ml-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[11px] font-medium uppercase tracking-wider transition-crisp",
                      selectedIndices.size > 0
                        ? "border-brand-500 bg-brand-50 text-brand-800 hover:bg-brand-100"
                        : "border-ink-200 bg-white text-ink-600 hover:border-ink-300 hover:bg-ink-50",
                    )}
                    data-testid="tool-step3-bulk-select-all"
                    aria-pressed={selectedIndices.size > 0}
                    title={
                      selectedIndices.size === rows.length
                        ? "Auswahl-Modus verlassen"
                        : "Auswahl-Modus aktivieren"
                    }
                  >
                    <span
                      className={cn(
                        "inline-block h-3 w-3 rounded-sm border transition-crisp",
                        selectedIndices.size === rows.length
                          ? "border-brand-500 bg-brand-500"
                          : selectedIndices.size > 0
                            ? "border-brand-500 bg-brand-200"
                            : "border-ink-300 bg-white",
                      )}
                    />
                    {selectedIndices.size === rows.length
                      ? "Auswahl aufheben"
                      : selectedIndices.size > 0
                        ? `${selectedIndices.size}/${rows.length} ausgewählt`
                        : "Auswahl-Modus"}
                  </button>
                )}
              </div>
              {rows.length > 0 && (
                <div
                  className="text-right leading-tight"
                  data-testid="tool-step3-summary"
                >
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-0.5 inline-flex items-center gap-1.5 justify-end">
                    {/* Iter 20.03.P5-fix (Runde 2) — Recalc-Pulse-Indicator.
                        Ersatz für die ehemalige 2 px Progress-Bar über der
                        Row-Liste. Pulsierender Sky-Dot neben dem KPI-Label
                        → keine Layout-Verschiebung (Element ist absolut
                        positioniert im inline-flex-Container, Fallback-
                        Space ist ein 8 px Placeholder auch bei idle,
                        damit die Buchstaben-Position stabil bleibt). */}
                    <span
                      data-testid="tool-step3-recalc-pulse"
                      data-status={autoRecalc.status === "running" ? "running" : "idle"}
                      className="relative inline-block h-2 w-2"
                      aria-hidden={autoRecalc.status !== "running"}
                      title={autoRecalc.status === "running" ? "Wird aktualisiert…" : ""}
                    >
                      {autoRecalc.status === "running" && (
                        <>
                          <span className="absolute inset-0 rounded-full bg-sky-400 opacity-75 animate-ping" />
                          <span className="absolute inset-0 rounded-full bg-sky-500" />
                        </>
                      )}
                    </span>
                    Anrechenbar gesamt
                  </div>
                  {/* Iter 20.03.P5-fix Runde 11 (Layout-Stabilität) —
                      Ehemals wurde bei isDirty der große Zahlen-Block
                      durch einen kleineren kursiven „Neuberechnung
                      nötig"-Text ersetzt, wodurch die Kachel schrumpfte
                      und darunter alle Rows nach oben sprangen. Fix:
                      Zahl bleibt IMMER sichtbar mit konstanter Größe.
                      Bei dirty/running wird sie lediglich leicht
                      ausgegraut (opacity-50) — der pulsierende Sky-Dot
                      neben dem KPI-Label übernimmt die Signalisierung
                      dass gerade neu gerechnet wird. */}
                  <div
                    className={cn(
                      "font-cabinet text-2xl sm:text-3xl tracking-tight text-brand-600 font-medium tabular-nums transition-opacity",
                      isDirty ? "opacity-50" : "opacity-100",
                    )}
                    data-testid="tool-step3-total"
                    title={
                      isDirty
                        ? "Wert wird gerade aktualisiert"
                        : undefined
                    }
                  >
                    {finalAggregate.display}
                  </div>
                  {/* Iter 20.03 Runde 16 (User-Feedback 2026-02-21) — Statt
                      der 3-teiligen Aufschlüsselung („Vordienst + Ausbildung
                      + Sonderregel") zeigen wir nur noch eine einzige
                      Bruttosumme. Die detaillierte Zerlegung ist eh im
                      „Wie wird berechnet?"-Modal verfügbar.
                      Runde 16-fix — Zeile wird IMMER gerendert, sobald eine
                      Summe > 0 vorhanden ist. Vorher gab es einen Guard
                      „nur bei Brutto > Netto", der aber genau die Fälle
                      unter Cap-Grenze ausblendete — der User erwartet die
                      Brutto-Info aber konsistent an dieser Stelle. */}
                  {(() => {
                    const rohDays = aggregateRoh?.totalDays || 0;
                    const bonusDays =
                      (normalBonusMonthsActive + bypassMonthsActive) * 30;
                    const bruttoDays = rohDays + bonusDays;
                    if (bruttoDays <= 0) return null;
                    const bJ = Math.floor(bruttoDays / 360);
                    const bM = Math.floor((bruttoDays % 360) / 30);
                    const bT = bruttoDays % 30;
                    const parts = [];
                    if (bJ) parts.push(`${bJ} J.`);
                    if (bM) parts.push(`${bM} M.`);
                    if (bT || (!bJ && !bM)) parts.push(`${bT} T.`);
                    const bruttoDisplay = parts.join(" ");
                    return (
                      <div
                        className={cn(
                          "text-[11px] text-warm-500 mt-0.5 tabular-nums transition-opacity",
                          isDirty ? "opacity-50" : "opacity-100",
                        )}
                        data-testid="tool-step3-brutto-summary"
                        title={`Bruttosumme vor Anwendung der KV-Höchstanrechnungsdauer und vor Kombination mit Bonuszeiten. Detail siehe "Wie wird berechnet?".`}
                      >
                        Brutto: {bruttoDisplay}
                      </div>
                    );
                  })()}
                  {/* Iter 19.63.101 — Cap-Bypass-Vordienst-Aggregat */}
                  {bypassVordienstAggregate.totalDays > 0 && (
                    <div
                      className={cn(
                        "text-[11px] text-amber-700 mt-0.5 tabular-nums transition-opacity",
                        isDirty ? "opacity-50" : "opacity-100",
                      )}
                      data-testid="tool-step3-cap-bypass-breakdown"
                      title="Anteil aus Sonderregel-Positionen (Selber Arbeitgeber, zählt zusätzlich zur KV-Höchstanrechnungsdauer)"
                    >
                      + {bypassVordienstAggregate.display} <span className="italic">(Selber Arbeitgeber)</span>
                    </div>
                  )}
                  {/* Iter 20.03 Runde 16 — Die frühere „Gesamt: {aggregateRoh.display}"-
                      Zeile bei Cap-Hit ist entfernt, weil sie redundant zu der
                      neuen einheitlichen „Brutto: X"-Zeile darüber ist. Der
                      Vordienst-Roh-Anteil ist im Brutto-Total mit enthalten. */}
                  <div className="text-[11px] text-warm-600 mt-1">
                    {/* Iter 19.63.5 — Wenn Konsolidierung stattfand: 57 (kons. 51).
                        Wenn keine: nur 51 Zeilen. So sieht der User dass alle
                        SV-Auszug-Zeiträume erkannt wurden. */}
                    {aiInfo?.consolidation && aiInfo.consolidation.merges > 0 ? (
                      <>
                        {aiInfo.consolidation.rawCount} Zeile
                        {aiInfo.consolidation.rawCount === 1 ? "" : "n"}
                        {" "}<span className="text-warm-500">· kons. auf {aiInfo.consolidation.mergedCount}</span>
                      </>
                    ) : (
                      <>
                        {rows.length} Zeile{rows.length === 1 ? "" : "n"}
                      </>
                    )}
                    {capMonths > 0 && (
                      <>
                        {" "}· max.{" "}
                        {bypassMonthsActive > 0 ? (
                          <span
                            title={`${capYears} Jahre lt. KV + ${Math.round(bypassMonthsActive / 12)} Jahre KV-Sonderregel`}
                            data-testid="cap-with-bypass-label"
                          >
                            <span className="text-warm-500 line-through">{capYears}</span>
                            {" "}<span className="text-amber-700 font-medium">{effectiveCapYears} Jahre</span>
                            {" "}<span className="text-amber-700">lt. KV (+{Math.round(bypassMonthsActive / 12)} Jahre Sonderregel)</span>
                          </span>
                        ) : (
                          <>{capYears} Jahre lt. KV</>
                        )}
                      </>
                    )}
                  </div>
                  {/* Iter 19.63.4 / Iter 20.03.P5-fix Runde 7 / Runde 12 —
                      Berechnungsdetails-Trigger. Runde 12: Statt Conditional-
                      Rendering (isDirty toggelt sichtbar) verwenden wir jetzt
                      `visibility: hidden`, damit die Kachel-Höhe konstant
                      bleibt und weiter unten liegende Content-Bereiche nicht
                      springen, wenn der Auto-Recalc läuft. */}
                  {/* Iter 19.63.4 / Iter 20.03.P5-fix Runde 7 / Runde 12 /
                      Runde 13 — Berechnungsdetails-Trigger.
                      Runde 13: Der Button wird IMMER gerendert, sobald
                      Zeilen existieren — der Kachel-Höhe stabilisierende
                      Effekt via `visibility: hidden` wirkt auch dann,
                      wenn temporär keine Zeile anrechenbar ist (z.B. wenn
                      der User alle deaktiviert hat oder bei Zwischen-
                      zuständen während des Recalcs). Verhindert den
                      32-px-Kachel-Sprung, den der Testing-Agent gemessen
                      hat. */}
                  {rows.length > 0 && (() => {
                    const hasAnrechenbar = rows.some((r) => r.anrechenbar);
                    const hidden = isDirty || !hasAnrechenbar;
                    return (
                      <button
                        type="button"
                        onClick={() => setShowBerechnung(true)}
                        data-testid="berechnungsdetails-open-btn"
                        className={cn(
                          "mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-brand-300 bg-white text-brand-700 hover:bg-brand-50 hover:border-brand-500 hover:text-brand-800 transition-crisp text-[11px] font-semibold uppercase tracking-wider",
                          hidden ? "invisible pointer-events-none" : "visible",
                        )}
                        aria-hidden={hidden}
                        tabIndex={hidden ? -1 : undefined}
                      >
                        <Calculator size={13} weight="bold" />
                        Wie wird berechnet?
                      </button>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Iter 19.68.3 / Iter 20.03.P5-fix Runde 7 — Quality-Warning-
                Banner. Ehemals immer voll expandiert mit langem
                Erklärungstext + Chip-Liste. Nach UX-Audit (P0 Amber-
                Inflation + P1 Chip-Duplikate): collapse-by-default,
                Zähler-Chip führt zur Erklärung, Ziel-Chips sind
                dedupliziert (`NICHICON ×3` statt 3× dieselbe Kachel).
                Klick auf einen Ziel-Chip scrollt nach wie vor zur
                betroffenen Row (`vd-row-anchor-${idx}`). */}
            {(() => {
              const unresolved = (rows || [])
                .map((r, i) => ({ row: r, idx: i }))
                .filter(({ row }) => hasUnresolvedQualityWarning(row));
              if (unresolved.length === 0) return null;
              // Chips deduplizieren nach Label → { label, ids: [idx…] }
              const bucket = new Map();
              for (const { row, idx } of unresolved) {
                const ag = (row.arbeitgeber || "").trim();
                const label = ag.length > 0 ? ag : `Position ${idx + 1}`;
                if (!bucket.has(label)) bucket.set(label, []);
                bucket.get(label).push(idx);
              }
              const chips = Array.from(bucket.entries()).map(([label, ids]) => ({
                label,
                count: ids.length,
                firstIdx: ids[0],
              }));
              return (
                <div
                  data-testid="step3-quality-warning-banner"
                  className="mb-3 border border-amber-300 bg-amber-50/60 rounded-sm text-[12px] text-amber-900"
                >
                  <button
                    type="button"
                    onClick={() => setQualityWarningExpanded((v) => !v)}
                    data-testid="step3-quality-warning-toggle"
                    aria-expanded={qualityWarningExpanded}
                    className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-amber-100/60 transition-crisp cursor-pointer text-left"
                  >
                    <Warning size={16} weight="fill" className="shrink-0 text-amber-600" />
                    <span className="font-semibold flex-1">
                      {unresolved.length === 1
                        ? "1 Position mit Auslese-Unsicherheit"
                        : `${unresolved.length} Positionen mit Auslese-Unsicherheit`}
                    </span>
                    <span className="text-[11px] text-amber-700 font-medium">
                      {qualityWarningExpanded ? "Details ausblenden" : "Details einblenden"}
                    </span>
                    <CaretDown
                      size={14}
                      weight="bold"
                      className={`shrink-0 text-amber-700 transition-transform ${qualityWarningExpanded ? "rotate-180" : ""}`}
                    />
                  </button>
                  {qualityWarningExpanded && (
                    <div className="px-3 pb-3 pt-1 leading-snug border-t border-amber-200/70">
                      ET war sich beim automatischen Auslesen aus dem Quelldokument an{" "}
                      {unresolved.length === 1 ? "dieser Stelle" : "diesen Stellen"}{" "}
                      nicht sicher. Bitte den jeweiligen Eintrag gegen das Originaldokument
                      prüfen — entweder die Daten korrigieren oder den Eintrag über{" "}
                      <span className="font-medium">&bdquo;Position geprüft &amp; bestätigt&ldquo;</span>{" "}
                      aktiv freigeben. Ungeprüfte Positionen werden in Schritt 4 und im
                      PDF-Export als solche markiert.
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {chips.map(({ label, count, firstIdx }) => (
                          <button
                            key={label}
                            type="button"
                            data-testid={`step3-quality-warning-link-${firstIdx}`}
                            onClick={() => {
                              const el = document.getElementById(`vd-row-anchor-${firstIdx}`);
                              if (el && typeof el.scrollIntoView === "function") {
                                el.scrollIntoView({ behavior: "smooth", block: "center" });
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-amber-400 bg-white text-amber-900 hover:bg-amber-100 transition-crisp text-[11px] font-medium"
                          >
                            {label}
                            {count > 1 && (
                              <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-[14px] px-1 rounded-full bg-amber-500 text-white text-[9.5px] font-bold leading-none">
                                ×{count}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Iter 19.63.65 — Evidence-Warning-Banner ganz oben.
                Sichtbar wenn mindestens ein Ausbildungs-Auto-Match mit
                `evidence==="weak"` UND `anrechenbar===false` existiert
                (unbearbeitete Warnung). Sobald der User bewusst das
                Häkchen setzt, verschwindet der Eintrag aus der Liste —
                und bei null Einträgen verschwindet der Banner ganz. */}
            {(() => {
              const weakUnresolved = (ausbildungszeiten || []).filter(
                (a) => a?.evidence === "weak" && !a.anrechenbar,
              );
              if (weakUnresolved.length === 0) return null;
              const labels = weakUnresolved.map((a) => `„${a.label}"`).join(", ");
              return (
                <div
                  data-testid="step3-evidence-warning-banner"
                  className="mb-3 flex items-start gap-2.5 border border-amber-300 bg-amber-50 px-3 py-2.5 rounded-sm text-[12px] text-amber-900 leading-snug"
                >
                  <Warning size={16} weight="fill" className="shrink-0 mt-0.5 text-amber-600" />
                  <div>
                    <div className="font-semibold mb-0.5">
                      Ausbildung erkannt &mdash; Abschluss nicht eindeutig belegt
                    </div>
                    In den hochgeladenen Dokumenten wurde eine Ausbildung
                    erkannt (z.&nbsp;B. Lehre, HAK, Studium), aber das
                    zugehörige Abschluss-Zeugnis (Lehrabschluss-Zeugnis,
                    Maturazeugnis, Sponsionsurkunde, &hellip;) konnte nicht
                    identifiziert werden.
                    <div className="mt-1">
                      <span className="font-medium">Betroffene Einträge:</span>{" "}
                      {labels}
                    </div>
                    <div className="mt-1">
                      Bitte das Häkchen bei &bdquo;Anrechenbar&ldquo; nur
                      setzen, wenn der Abschluss tatsächlich vorliegt.
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Iter 19.43.7 / Runde 10 — Legacy-Dirty-Banner entfernt.
                Auto-Recalc-Flag ist in Preview + Produktion permanent AN,
                daher wurde der manuelle Fallback-Hinweis-Text entfernt.
                Für den Fall dass jemand den Feature-Flag deaktiviert, gilt
                jetzt: keine Recalc-Aufforderung mehr im UI, weil kein
                manueller Trigger-Button mehr existiert. */}

            {/* Iter 20.03.P5-fix (Runde 2, Martina-Fall-4-Report) —
                Auto-Recalc-Status-Bar HIER ENTFERNT. Ersetzt durch ein
                pulsierendes Sky-Dot direkt neben dem „Anrechenbar gesamt"-
                Label in der KPI-Zusammenfassung, damit die Row-Liste
                sich NIE mehr verschiebt (auch nicht durch die 2 px
                Progress-Bar aus Runde 1). Siehe `tool-step3-recalc-pulse`
                in der Summary-Card. */}

            {/* Iter 19.63.11 — Cap-Warn-Banner entfernt auf User-Wunsch.
                Information ist via Tooltip am Cap-Label + via Berechnungs-
                details-Modal weiterhin verfügbar; das Banner war zu auffällig. */}

            {/* Iter 20.03.P5-fix Runde 4/5/6 — Konsolidierter KV-Eigenheiten-
                Banner. Zeigt Gleichwertigkeitsprüfung UND Sonderregeln
                (Vorarbeiterzeit / Lückenregel / gleiche Verwendungsgruppe)
                in EINEM Block. Ersetzt den vormaligen `step3-equivalence-
                banner` und den separaten `tool-step3-sonderregeln-hinweis`
                aus Runde 4-a.
                Iter 20.03.P5-fix Runde 6 — Sichtbarkeit erst NACH der
                Auswertung: Ohne Zeilen (`rows.length === 0`) fehlt der
                Kontext, in dem die Sonderregel per-Row-Detail-Bereich
                aktiviert werden müsste. Der User meldete, dass der Banner
                im leeren Vor-Extract-Zustand fälschlich schon sichtbar
                war. */}
            {rows.length > 0 && (
              <KvHinweisBanner
                equivalenceRequired={Boolean(selectedKv?.equivalence_check_required)}
                hasEquivalenceRowsToReview={
                  Boolean(selectedKv?.equivalence_check_required)
                    && rows.some((r) => needsEquivalenceReview(r, true))
                }
                sonderregeln={selectedKv?.cap_bypass_sonderregeln || EMPTY_ARR}
                zusatzHinweis={selectedKv?.zusatz_hinweis || ""}
                equivalenceTerminology={selectedKv?.equivalence_terminology || "gleichwertigkeit"}
              />
            )}

            {/* Iter 19.63.68 / 19.63.74 / 19.63.76 — Praktikum-Action-Banner.
                Sichtbar nur solange mindestens ein Praktikum noch keinen
                Anstellungs-Typ zugewiesen bekommen hat. Klick auf den
                Eintrag scrollt direkt zur betroffenen Zeile (Sprung-Link
                via Anchor-ID). Sobald der User pro Zeile zwischen
                „Angestellter" und „Arbeiter" gewählt hat (über den
                Inline-Selector in der Row), verschwindet der Banner.

                Hintergrund: KV-Regelwerke werten Praktika als anrechenbares
                Dienstverhältnis. Aus Lebensläufen ist nicht erkennbar, ob
                das Praktikum als Angestellten- oder Arbeiterverhältnis
                geführt wurde — der User muss das aktiv wählen, danach
                setzt die KV-Regel den Faktor automatisch. */}
            {(() => {
              const praktikumRows = rows
                .map((r, idx) => ({ r, idx }))
                .filter(({ r }) => hasUnresolvedPraktikum(r));
              if (praktikumRows.length === 0) return null;
              return (
                <div
                  data-testid="step3-praktikum-warning-banner"
                  className="mb-3 flex items-start gap-2.5 border border-amber-300 bg-amber-50 px-3 py-2.5 rounded-sm text-[12px] text-amber-900 leading-snug"
                >
                  <Warning size={16} weight="fill" className="shrink-0 mt-0.5 text-amber-600" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold mb-0.5">
                      {praktikumRows.length === 1
                        ? "1 Praktikum offen — bitte Anstellungs-Typ wählen"
                        : `${praktikumRows.length} Praktika offen — bitte Anstellungs-Typ wählen`}
                    </div>
                    Aus Lebensläufen ist nicht erkennbar, ob das Praktikum
                    als Angestellten- oder Arbeiterverhältnis geführt wurde.
                    Bitte pro Zeile zwischen &bdquo;Angestellter&ldquo; und &bdquo;Arbeiter&ldquo;
                    wählen &mdash; der Anrechnungsfaktor wird laut hinterlegter
                    KV-Regel automatisch gesetzt.
                    <div
                      className="mt-1.5 flex flex-wrap gap-1.5"
                      data-testid="step3-praktikum-jump-links"
                    >
                      {praktikumRows.map(({ r, idx }) => {
                        const ag = (r.arbeitgeber || "").trim();
                        const taet = (r.taetigkeit || "").trim();
                        const year =
                          (r.datum_von || "").slice(0, 4) ||
                          (r.datum_bis || "").slice(0, 4);
                        const head = ag || taet || "Unbenannte Position";
                        const label = year ? `${head}, ${year}` : head;
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              const el = document.getElementById(`vd-row-anchor-${idx}`);
                              if (el && typeof el.scrollIntoView === "function") {
                                el.scrollIntoView({ behavior: "smooth", block: "center" });
                              }
                            }}
                            data-testid={`step3-praktikum-jump-${idx}`}
                            className="px-2 py-0.5 text-[11px] font-medium rounded-sm border border-amber-400 bg-white text-amber-900 hover:bg-amber-100 transition-crisp"
                          >
                            &rarr; {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Mockup→Live Phase 5 — Bulk-Action-Bar (nur sichtbar wenn
                Flag aktiv UND mindestens eine Zeile selektiert ist). */}
            {bulkActionsEnabled && selectedIndices.size > 0 && (
              <BulkActionBar
                selectedCount={selectedIndices.size}
                onClearSelection={handleClearSelection}
                onBulkApply={handleBulkApply}
              />
            )}

            {/* Mockup→Live Phase 3 — Zeitleisten-Widget.
                Optionaler Ausklapp-Bereich, sichtbar nur wenn Feature-Flag
                aktiv UND mindestens eine Position vorhanden. Wahl (auf/zu)
                wird in localStorage persistiert. */}
            {timelineEnabled && rows.length > 0 && (
              <div className="mb-3" data-testid="tool-step3-timeline-wrap">
                <button
                  type="button"
                  onClick={toggleTimeline}
                  data-testid="tool-step3-timeline-toggle"
                  aria-expanded={showTimeline}
                  className="mb-2 inline-flex items-center gap-1.5 rounded-sm border border-ink-200 bg-white px-2.5 py-1 text-[11px] font-medium text-ink-700 hover:bg-ink-50 hover:border-ink-300 transition-crisp"
                >
                  {showTimeline ? (
                    <ArrowLeft size={12} weight="bold" className="rotate-90" />
                  ) : (
                    <ArrowRight size={12} weight="bold" className="rotate-90" />
                  )}
                  Zeitleiste {showTimeline ? "ausblenden" : "einblenden"}
                </button>
                {showTimeline && (
                  <Timeline
                    rows={rows}
                    capMonths={capMonths}
                    stichtag={stichtag}
                    requiresEquivalenceCheck={Boolean(selectedKv?.equivalence_check_required)}
                    equivalenceTerminology={selectedKv?.equivalence_terminology || "gleichwertigkeit"}
                    faktorRegeln={selectedKv?.faktor_regeln || EMPTY_OBJ}
                  />
                )}
              </div>
            )}

            {rows.length === 0 ? (
              <div
                data-testid="tool-step3-empty"
                className="border border-dashed border-warm-300 bg-warm-50 rounded-sm px-4 py-6 text-center text-sm text-warm-600 flex flex-col items-center gap-3"
              >
                <div>
                  Noch keine Beschäftigungszeiten.
                  <br />
                  Laden Sie oben einen Versicherungsdatenauszug hoch oder fügen Sie manuell eine Zeile hinzu.
                </div>
                <button
                  type="button"
                  onClick={addManualRow}
                  data-testid="tool-step3-add-manual-row"
                  className="inline-flex items-center justify-center gap-2 border border-dashed border-ink-300 hover:border-brand-400 bg-white hover:bg-brand-50/40 text-ink-600 hover:text-brand-700 rounded-sm py-2 px-4 text-[12px] font-medium transition-crisp"
                  title="Eine leere Beschäftigungszeit-Zeile anlegen"
                >
                  <Plus size={14} weight="bold" />
                  Zeile hinzufügen
                </button>
              </div>
            ) : (() => {
              // Iter 19.68.7 — Filter-Chips + gefilterte Anzeige.
              // Math/Payload/PDF nutzen weiterhin `rows` (= alle), Filter
              // ist rein visuell.
              const requiresEq = Boolean(selectedKv?.equivalence_check_required);
              const counts = {
                all: rows.length,
                anrechenbar: 0,
                not_anrechenbar: 0,
                needs_attention: 0,
              };
              for (const r of rows) {
                if (r?.anrechenbar === true) counts.anrechenbar++;
                else if (r?.anrechenbar === false) counts.not_anrechenbar++;
                if (rowNeedsAttention(r, { requiresEquivalenceCheck: requiresEq })) {
                  counts.needs_attention++;
                }
              }
              const matchesFilter = (r) => {
                if (vordienstFilter === "all") return true;
                if (vordienstFilter === "anrechenbar") return r?.anrechenbar === true;
                if (vordienstFilter === "not_anrechenbar") return r?.anrechenbar === false;
                if (vordienstFilter === "needs_attention") {
                  return rowNeedsAttention(r, { requiresEquivalenceCheck: requiresEq });
                }
                return true;
              };
              const visibleIndices = rows
                .map((r, i) => ({ r, i }))
                .filter(({ r }) => matchesFilter(r));
              return (
                <>
                  <FilterChips
                    active={vordienstFilter}
                    counts={counts}
                    onChange={setVordienstFilter}
                  />
                  {visibleIndices.length === 0 ? (
                    <div
                      data-testid="tool-step3-empty-filtered"
                      className="border border-dashed border-warm-300 bg-warm-50 rounded-sm px-4 py-5 text-center text-sm text-warm-600 flex flex-col items-center gap-2"
                    >
                      <span>Keine Position mit aktivem Filter.</span>
                      <button
                        type="button"
                        data-testid="tool-step3-empty-filtered-reset"
                        onClick={() => setVordienstFilter("all")}
                        className="inline-flex items-center px-2.5 py-1 rounded-sm border border-warm-400 bg-white text-warm-800 hover:bg-warm-100 transition-crisp text-[11.5px] font-medium"
                      >
                        Filter zurücksetzen
                      </button>
                    </div>
                  ) : (
                    <ul className="space-y-2" data-testid="tool-step3-rows">
                      {visibleIndices.map(({ r, i }) => (
                        <VordienstRow
                          key={i}
                          index={i}
                          row={r}
                          stichtag={stichtag}
                          onChange={updateRow}
                          onRemove={removeRow}
                          requiresEquivalenceCheck={requiresEq}
                          equivalenceTerminology={selectedKv?.equivalence_terminology || "gleichwertigkeit"}
                          faktorRegeln={selectedKv?.faktor_regeln || EMPTY_OBJ}
                          praktikumRegeln={selectedKv?.praktikum_regeln || EMPTY_OBJ}
                          capBypassRules={capBypassRules}
                          isCapReached={isCapReached}
                          capBypassSonderregeln={selectedKv?.cap_bypass_sonderregeln || EMPTY_ARR}
                          mode={compactRowEnabled && compactMode ? "compact" : "detail"}
                          selected={bulkActionsEnabled && selectedIndices.has(i)}
                          onSelectToggle={
                            bulkActionsEnabled && compactRowEnabled && compactMode
                              ? handleSelectToggle
                              : null
                          }
                        />
                      ))}
                    </ul>
                  )}
                  {/* Iter 20.03.P5-fix Runde 10 — „+ Zeile hinzufügen"-
                      Trigger direkt unter der Row-Liste (statt im Upload-
                      Panel). Immer sichtbar, sobald überhaupt Zeilen
                      existieren (auch bei gefilterter Ansicht → damit
                      der User nicht im leeren Filter-Zustand strandet).
                      Klick fügt eine leere `EmptyRow()` ans Ende an. */}
                  {rows.length > 0 && (
                    <button
                      type="button"
                      onClick={addManualRow}
                      data-testid="tool-step3-add-manual-inline"
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-dashed border-ink-300 hover:border-brand-400 bg-white hover:bg-brand-50/40 text-ink-600 hover:text-brand-700 rounded-sm py-2.5 text-[12px] font-medium transition-crisp"
                      title="Eine leere Beschäftigungszeit-Zeile ans Ende der Liste anhängen"
                    >
                      <Plus size={14} weight="bold" />
                      Zeile hinzufügen
                    </button>
                  )}
                </>
              );
            })()}
          </div>

          {/* Ausbildungs-bedingte Vordienstzeiten — versteckt by default,
              sichtbar wenn KI gematcht hat oder User explizit klickt. */}
          <AusbildungsBlock
            regeln={selectedKv?.ausbildungsregeln || []}
            rows={ausbildungszeiten || []}
            setRows={setAusbildungszeiten || (() => {})}
          />
        </div>
      </div>

      <div className="px-8 py-5 border-t border-ink-200 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 bg-ink-50">
        <Button variant="secondary" size="lg" onClick={onBack} data-testid="tool-step3-back">
          <ArrowLeft size={14} weight="bold" />
          Zurück
        </Button>
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3">
          {/* Iter 20.03.P5-fix Runde 10 — „Zurücksetzen"-Button aus dem
              Upload-Panel in den Footer gewandert (links von „Weiter zu
              Schritt 4"). Nur sichtbar sobald es Zeilen zu resetten gibt. */}
          {rows.length > 0 && (
            <Button
              variant="ghost"
              size="lg"
              onClick={() => setResetDialogOpen(true)}
              data-testid="tool-step3-reset-footer"
              title="Eigene Änderungen verwerfen oder Schritt 3 komplett neu starten."
            >
              <ClockCounterClockwise size={14} weight="bold" />
              Zurücksetzen
            </Button>
          )}
          {/* Iter 20.03.P5-fix Runde 10 — Manueller Recalc-Trigger im
              Footer entfernt. Auto-Recalc uebernimmt die Rekalkulation
              nach jeder User-Aenderung automatisch. */}
          <Button
            variant="brand"
            size="lg"
            onClick={onNext}
            disabled={isDirty || extracting || reviewing || recalculating}
            title={
              extracting || reviewing || recalculating
                ? "Bitte warten — ET wertet die Dokumente gerade aus."
                : isDirty
                ? "Bitte kurz warten — die Anrechnung wird automatisch neu berechnet."
                : undefined
            }
            data-testid="tool-step3-next"
          >
            <Sparkle size={14} weight="bold" />
            Empfehlung erstellen
          </Button>
        </div>
      </div>

      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        blobs={sourceBlobs}
        rows={rows}
        stichtag={stichtag}
        /* Iter 19.63.19 — display fix: use authoritative final aggregate
           (backend Source-of-Truth + bypass), not the recomputed frontend
           sum which diverges by per-row rounding. */
        totalAnrechenbarDisplay={finalAggregate?.display || ""}
        capMonths={capMonths}
        capYears={capYears}
      />

      {/* Iter 19.63.4 — Berechnungsdetails Modal */}
      <BerechnungsdetailsModal
        open={showBerechnung}
        onClose={() => setShowBerechnung(false)}
        rows={rows}
        ausbildungszeiten={ausbildungszeiten}
        aggregate={finalAggregate}
        capMonths={capMonths}
        stichtag={stichtag}
        capBypassRules={capBypassRules}
        bypassVordienstTotalDays={bypassVordienstAggregate.totalDays}
      />

      {/* Iter 19.63.66 — Reset-Dialog (Soft/Hard Reset) */}
      <ResetDialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        onResetEdits={handleSoftReset}
        onResetAll={handleHardReset}
        hasFiles={files.length > 0}
        hasRows={rows.length > 0}
        busy={extracting || reviewing || recalculating}
      />
    </section>
  );
}
